import * as THREE from 'three';
import {
  MOTION_TIMING,
  STUDENT_NAME_MAX,
  WORLD_SLUG,
  directionKey,
  hasChange,
  hasStudentName,
  isFurnishLesson,
  isPanoLesson,
  modelFootprint,
  modelSlugOf,
  stepSeconds,
  modelSeconds,
  towardDirection,
  worldStateAt,
  type Cue,
  type CueMap,
  type FurnitureAssigned,
  type Lesson,
  type ModelAnimation,
  type ModelCatalogue,
  type PanoVariants,
  VILLA_SPACE,
  type PlacementSpace,
  type ServedWorld,
  type Step,
  type WorldSlug,
} from '@vrlearning/lesson-schema';
import { LessonPlayer, type StepPhase } from './engine/player.js';
import {
  GUIDE_STALL_SECONDS,
  PREDICTED_SECONDS,
  WORLD_ENTRY_VOICE_SECONDS,
  askedGuide,
  cueAt,
  cuesFrom,
  guideFor,
  outcomeSeconds,
  pacedPhase,
  pages,
  questionCue,
  worldEntry,
  type Guide,
  type GuideRender,
} from './engine/guide.js';
import { GuidePacer } from './engine/guide-pacer.js';
import { Autoplay, autoplayChoice, readingSeconds, type AutoplayMove } from './engine/autoplay.js';
import { unspokenNarration } from './engine/step-text.js';
import { StallWatch } from './engine/guide-stall.js';
import type { PlateContent } from './render/subtitle.js';
import { previewOptions } from './engine/eye-height.js';
import { StepPlacement } from './engine/step-placement.js';
import { REACH_METRES } from './engine/placement.js';
import { LocalFurnishing, sessionLink, type FurnishingLink } from './engine/furnishing-link.js';
import { placementSpaceOf } from './engine/placement-space.js';
import { FurnitureView } from './render/furniture/pieces.js';
import { FurnishLectern } from './render/furniture/furnish-lectern.js';
import { FurnishInteraction } from './render/furniture/interaction.js';
import { NoticePlate } from './render/furniture/notice.js';
import { FURNISH_PLACEMENT } from './render/lectern.js';
import { lockAnchor } from './engine/lock-anchor.js';
import { boundsOf, buildScene, type BuiltScene } from './render/scene.js';
import { STUDIO_HORIZON } from './render/studio.js';
import { shapeNote } from './render/shapes.js';
import { photoNote } from './render/posters.js';
import { evictUnsigned } from './media-cache.js';
import { assignmentPlan } from './prefetch/plan.js';
import { PrefetchRunner } from './prefetch/runner.js';
import { Prefetcher, preparingLine } from './prefetch/lobby.js';
import { browserRunnerDeps, lessonWorldFiles } from './prefetch/browser.js';
import { SessionFlow } from './prefetch/session-flow.js';
import { applyLook } from './render/look/look.js';
import { PerfMeter } from './render/perf.js';
import {
  NO_PANO_CAPS,
  capsLine,
  probeCaps,
  rendererCaps,
  type PanoCaps,
} from './render/pano-caps.js';
import { panoHudLines } from './render/pano-hud.js';
import { AudioEngine, soundPlanFor } from './render/audio/engine.js';
import {
  guideLabel,
  isGuideKey,
  isVoiceKey,
  missingNote,
  soundButton,
  voiceNote,
} from './render/audio/controls.js';
import {
  VoicePlayer,
  classVoice,
  voiceButtonShown,
  voiceStartsOn,
  voicesOf,
} from './render/audio/voice.js';
import { followXrVisibility, pageAudio } from './render/audio/page.js';
import { mayPrefetch, prefetchWorlds, sweepWorldCache } from './render/worlds/cache.js';
import { browserFetchers } from './render/worlds/fetchers.js';
import { PREVIEW_PICTURE, pictureAddress, sendPicture } from './dev/package-picture.js';
import { packagePreview } from './dev/package-preview.js';
import { Generation } from './engine/generation.js';
import {
  catalogueOfSource,
  prepareWorld,
  worldSourceFor,
  type WorldSource,
} from './render/worlds/source.js';
import { takenOverNote } from './render/worlds/taken-over.js';
import { Stage } from './render/stage.js';
import { Placeable } from './render/placeable.js';
import { AssemblyBench } from './render/assembly-bench.js';
import { LockButton } from './render/lock-button.js';
import {
  Dragger,
  HandleButton,
  Toolbar,
  handleNear,
  railAlong,
  settle,
  type Draggable,
  type HandleKind,
} from './render/handles.js';
import { LessonUi } from './render/ui.js';
import { FrameAids } from './render/frame-aids.js';
import { MeasureTool } from './render/measure.js';
import { DemoRoom } from './render/demo-room.js';
import { Surfaces } from './render/surfaces.js';
import { HotspotBadges, type DirectionEntry } from './render/badges.js';
import { featureBadgesFor } from './engine/feature-badges.js';
import { PANO_HINT } from './render/pano.js';
import { SceneMotion, pickable, shown } from './render/motion.js';
import { changeNote, isForwardByOne, modelsChange, replayOffered } from './engine/step-change.js';
import { ResultQueue, browserQueueStorage } from './engine/result-queue.js';
import { registerServiceWorker, setLessonRunning } from './pwa.js';
import {
  DeviceRevokedError,
  batteryLevel,
  checkIn,
  deviceFetch,
  pairDevice,
  revoked,
  storedIdentity,
  whenRevoked,
  type PairingProgress,
} from './api/pairing-client.js';
import {
  listLessons,
  loadLesson,
  type LessonSummary,
  type LoadedLesson,
} from './api/lessons-client.js';
import { keepPairingOnDevice } from './api/persistence.js';
import { forgetSchoolData } from './api/school-data.js';
import { rememberTrouble } from './api/trouble.js';
import { previewRequest, startPreview } from './preview.js';
import {
  VIEWER_TEXT,
  ViewerHost,
  fetchViewer,
  takeViewerRequest,
  type ViewerMessage,
} from './furniture-viewer.js';
import {
  PLAY_TEXT,
  fetchPlayLesson,
  playProblemText,
  takePlayRequest,
  type PlayStart,
} from './play.js';
import demoBuild from 'virtual:demo-lesson';
import demoMedia from 'virtual:demo-media';
import demoCues from 'virtual:demo-cues';
import demoGuide from 'virtual:demo-guide';
import demoModelsBuild from 'virtual:demo-models';
import demoPlay from 'virtual:demo-play';
import demoWorldsBuild from 'virtual:demo-worlds';
import {
  DEMO_TEXT,
  demoLesson,
  demoModels,
  demoProblemText,
  demoWorlds,
  type DemoOutcome,
} from './demo.js';
import { controlFor, followsClass } from './engine/following.js';
import {
  browserSocket,
  fetchTicket,
  SessionClient,
  guideOfState,
  sameAssignment,
  soundOf,
  spannendOf,
  type Assignment,
  type LiveState,
  type PreviewOpen,
} from './api/session-client.js';
import { ClassClock } from './api/class-clock.js';
import { ModelRuntime, type SpannendEvent } from './render/models/runtime.js';
import { parseModel } from './render/models/builder.js';
import { ModelSoundBank } from './render/audio/model-sound.js';
import { recordingsOf } from './render/audio/files.js';
import { SPANNEND, SPANNEND_TEXT, SpannendSwitch, hasSpannend } from './render/models/spannend.js';
import { spannendButton } from './render/models/spannend-button.js';
import {
  MODEL_VIEWER_TEXT,
  ModelViewerHost,
  fetchModelViewer,
  takeModelViewerRequest,
  type ModelViewerMessage,
} from './model-viewer.js';
import {
  WORLD_VIEWER_TEXT,
  WorldViewerHost,
  fetchWorldViewer,
  parentOrigin,
  takeWorldViewerRequest,
  type WorldViewerMessage,
  type WorldViewerStart,
} from './world-viewer.js';
import {
  LESSON_PICTURE,
  LESSON_PICTURE_TEXT,
  LessonPictureHost,
  SceneIncomplete,
  fetchLessonPicture,
  isBlank,
  pictureJpeg,
  takeLessonPictureRequest,
  type LessonPictureMessage,
  type LessonPictureStart,
} from './lesson-picture.js';
import { PICTURE_FRAME, pictureRoute, pictureStep } from './engine/picture-step.js';
import { catalogueOfServed } from './api/served-catalogue.js';
import { CREDITS_TEXT, creditsText, lessonCredits } from './render/models/credits.js';
import {
  TEST_DRIVE_TEXT,
  fetchTestDrive,
  takeTestDriveRequest,
  type TestDriveAnswer,
  type TestDriveStart,
} from './test-drive.js';
import { fetchQuestPreview, type QuestPreviewAnswer } from './bekijken/fetch.js';
import { HeadsetMode } from './bekijken/headset-mode.js';
import { BEKIJKEN_IDLE_MS, IdleTimer } from './bekijken/idle.js';
import { hangBekijken } from './bekijken/mode.js';
import { bekijkenPlan } from './bekijken/plan.js';
import { playLessonPreview } from './bekijken/lesson-preview.js';
import { PreviewMarker, lecternPanelTop } from './render/preview-marker.js';
import { BEKIJKEN_TEXT } from './bekijken/text.js';
import { disposeFigure, makeReferenceFigure } from './render/reference-figure.js';
import { TestPanel } from './render/test-panel.js';
import { pressStep, testPanelRows } from './engine/test-panel.js';
import { placeTestPanel, type PanelPlace, type WorldPose } from './engine/test-panel-place.js';
import { ViewExtras, groundedAt } from './engine/view-extras.js';

const overlay = document.getElementById('overlay')!;
const hud = document.getElementById('hud')!;

/** `?eye=`, `?frame=1`, `?look=flat`, `?perf=1` and `?world=`, for the desktop preview (engine/eye-height.ts). */
const desktop = previewOptions();
/**
 * The address the page was opened with, `?sound=0` and all: a played lesson
 * takes its ticket off the address at once (takePlayRequest), and the query
 * with it, before its sound is made.
 */
const openedAt = location.href;

/**
 * The voice guide outside a class (ADR-035): practising alone, a played
 * lesson and the demo. `?guide=voice` (or `text`) on the address, else the
 * demo's build (`pnpm demo:build --guide`); the start card's "Begeleiding"
 * and the preview's G change it, and it is kept for "Opnieuw bekijken". In a
 * class the teacher's switch decides.
 */
let localGuide: Guide = askedGuide(openedAt) ?? demoGuide;

/**
 * Spannend outside a class (ADR-043): practising alone, a played lesson, the
 * desktop preview -- `?spannend=1`, the corner's switch and the start card;
 * calm by default, kept for "Opnieuw bekijken". In a class the teacher's
 * switch decides (`classSpannend`, from every session.state).
 */
// A demo built with `--spannend` starts with it on (`virtual:demo-play`; false in every other build).
let localSpannend = desktop.spannend || demoPlay.spannend;
let classSpannend = false;

/**
 * One queue for the whole app, not one per lesson: results outlive the lesson
 * that produced them, and have to survive a reload with the headset offline.
 */
const resultQueue = new ResultQueue(browserQueueStorage());

/**
 * The page's sound (world audio, ADR-034): one engine and, once something
 * plays, one AudioContext for the page's life, like the stage. Made by a
 * headset, a played lesson and a demo -- never by the console's preview of a
 * class, which stays silent.
 */
let audio: AudioEngine | undefined;

function startAudio(kind: 'class' | 'play' | 'demo'): AudioEngine {
  // A class headset never keeps a mute; a played lesson and the demo do (page.ts).
  audio ??= pageAudio(kind, {
    href: openedAt,
    create: () => new AudioContext({ latencyHint: 'playback' }),
  });
  return audio;
}

/** Files the dev-only fixtures hold in memory, by URL (dev/test-drive-fixture.ts); empty otherwise. */
const fixtureFiles = new Map<string, ArrayBuffer>();
addEventListener('pagehide', () => fixtureFiles.clear());

/** A world file's or a voice-over's bytes, through the service worker's cache. */
async function fetchBytes(url: string): Promise<ArrayBuffer> {
  const held = fixtureFiles.get(url);
  if (held) return held;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.arrayBuffer();
}

/**
 * The unpaired screen: shows a six-character code for a customer admin to type
 * into the console, and polls until the headset has a device token.
 *
 * docs/02-architecture.md: unpaired -> pairing -> lobby. This is the first two.
 */
function showPairing(): void {
  overlay.innerHTML = '';
  hud.textContent = '';

  const card = document.createElement('div');
  card.className = 'card';
  card.innerHTML = `
    <h1>Koppel deze headset</h1>
    <p>Geef deze code door aan de beheerder. Die voert hem in op het beheerscherm.</p>
    <div class="code" aria-live="polite">…</div>
    <p class="hint status">Een code aanvragen…</p>
  `;
  const codeElement = card.querySelector('.code')!;
  const status = card.querySelector('.status')!;
  overlay.append(card);

  const controller = new AbortController();

  const onProgress = (progress: PairingProgress): void => {
    switch (progress.status) {
      case 'code': {
        // Spaced in the middle: six characters read aloud across a classroom
        // are easier to keep place in as two groups of three.
        codeElement.textContent = `${progress.code.slice(0, 3)} ${progress.code.slice(3)}`;
        status.textContent = 'Wachten op de beheerder…';
        break;
      }
      case 'paired': {
        status.textContent = 'Gekoppeld.';
        // pairDevice cleared the stored results of any earlier school before
        // storing this token; the copy loaded into memory at start goes too,
        // or the first flush would hand them to this school's session.
        resultQueue.clear();
        showPicker();
        // The headset has a token for the first time, so this is the moment
        // it can join a session. Without it a freshly paired headset sits on
        // the lobby screen until somebody reloads the page.
        linkSession();
        // Who it is now (a school's headset or a platform test headset, ADR-058), before the lobby
        // asks for any lessons.
        {
          const identity = storedIdentity();
          if (identity) void checkInHere(identity);
        }
        // And the moment there is something worth keeping. Asking only at
        // boot would leave a freshly paired headset evictable until the next
        // restart, which for a headset that lives in a cupboard could be a
        // week.
        void keepPairingOnDevice();
        break;
      }
      case 'expired': {
        codeElement.textContent = '—';
        status.textContent = 'De code is verlopen.';
        addRetry(card, controller);
        break;
      }
      case 'error': {
        codeElement.textContent = '—';
        status.textContent = `Geen verbinding met de server. ${progress.message}`;
        addRetry(card, controller);
        break;
      }
    }
  };

  void pairDevice(onProgress, { signal: controller.signal });
}

function addRetry(card: HTMLElement, controller: AbortController): void {
  if (card.querySelector('.retry')) return;
  const retry = document.createElement('button');
  retry.className = 'lesson retry';
  retry.type = 'button';
  retry.textContent = 'Opnieuw proberen';
  retry.addEventListener('click', () => {
    controller.abort();
    showPairing();
  });
  card.append(retry);
}

/**
 * The lobby: waiting for the teacher, with the lesson list still there.
 *
 * The list is not a leftover. A paired headset normally sits here until a
 * lesson is pushed to it, but being able to start one by hand is how the
 * headset gets tested on its own, and how a teacher demonstrates without
 * opening the console.
 */
/** The lobby: what a paired headset shows when no lesson is running. */
function showPicker(): void {
  renderWaiting();
}

/**
 * What a student looks at between putting the headset on and the teacher
 * starting.
 *
 * One sentence, and nothing to do. Thirty children wait here for several
 * minutes at the start of every lesson, which makes it one of the most-read
 * screens in the product, and the version before this was a card doing three
 * jobs at once: a heading, a status line, a list of lessons to wander off
 * into, and a line of keyboard hints that mean nothing inside a headset.
 *
 * The headset's own name is here because it answers a question a teacher
 * actually asks out loud -- "which one are you?" -- and because a child who
 * can read it can tell an adult which headset is misbehaving.
 *
 * There is deliberately no "unpair" button. This screen is in front of a
 * student, and unpairing takes the headset out of the school's list and frees
 * a licence seat; a button doing that within reach of a class is a way to
 * lose headsets one at a time. It happens in the console instead, and the
 * API tells this headset -- at once over its socket, or at its next request --
 * which brings it back to the pairing screen by itself (unpairHere).
 */
function renderWaiting(): void {
  overlay.innerHTML = '';
  const card = document.createElement('div');
  card.className = 'card waiting';
  card.innerHTML = `
    <h1></h1>
    <p class="lead"></p>
    <p class="link"></p>
  `;
  card.querySelector('h1')!.textContent = platformHeadset
    ? BEKIJKEN_TEXT.platformTitle
    : 'Wachten op de leerkracht';
  card.querySelector('.lead')!.textContent = platformHeadset
    ? BEKIJKEN_TEXT.platformLead
    : waitingLead();

  // Why a preview ended or did not open (ADR-058), said once.
  if (lobbyNotice) {
    const notice = document.createElement('p');
    notice.className = 'hint notice';
    notice.textContent = lobbyNotice;
    card.append(notice);
    lobbyNotice = undefined;
  }

  // The name is not asked for here. It is asked for on the way into VR, with
  // the lesson's own title above it, because that is the moment a child is
  // paying attention to this screen -- a field offered minutes earlier, while
  // they are still sorting out the strap, gets filled in by whoever happens
  // to be holding the headset, or not at all.
  if (deviceName) {
    const who = document.createElement('p');
    who.className = 'hint';
    who.textContent = `Deze headset heet ${deviceName}.`;
    card.append(who);
  }

  // Only once the teacher has let them out of a lesson. Free time is
  // something a teacher gives; a list of things to start in the two minutes
  // before a lesson is an invitation to wander off.
  // Nor while the teacher is setting a lesson ready (ADR-038): held, as by a running session.
  if (freeTimeAllowed && !prefetcher.preparing && !platformHeadset) {
    const practise = document.createElement('button');
    practise.className = 'quiet';
    practise.type = 'button';
    practise.textContent = 'Zelf oefenen';
    practise.addEventListener('click', () => {
      void renderLessonList();
    });
    card.append(practise);
  }

  const worlds = document.createElement('p');
  worlds.className = 'hint worlds';
  worlds.textContent = worldsReady ? 'Werelden klaar' : '';
  card.append(worlds);

  overlay.append(card);
  updateLinkStatus();
  // Only once the check-in answered: until then a platform test headset (ADR-058) is not known as
  // one, and would ask for a school's lessons it is refused.
  if (checkedIn && !platformHeadset) void prefetchLibraryWorlds();
}

/** The lobby has fetched every world its lessons use (ADR-032). */
let worldsReady = false;
let prefetching = false;

/**
 * While a paired headset waits in the lobby, it fetches the worlds of the
 * lessons in its library, one file at a time, so a class does not pull them
 * all at a lesson's start. Stops the moment a lesson starts; tried again the
 * next time the lobby shows.
 */
async function prefetchLibraryWorlds(): Promise<void> {
  const identity = storedIdentity();
  // Not beside a lesson being prepared: that one fetches its own world first (ADR-038).
  if (!identity || prefetching || worldsReady || !mayPrefetch() || prefetcher.preparing) return;
  prefetching = true;
  try {
    let lessons: LessonSummary[];
    try {
      lessons = await listLessons(identity);
    } catch (error) {
      // Unlinked meanwhile: unpaired already, and nothing to fetch for.
      if (error instanceof DeviceRevokedError) return;
      throw error;
    }
    const slugs = [...new Set(lessons.map((lesson) => lesson.world))].filter(
      (world): world is WorldSlug => world !== undefined && WORLD_SLUG.test(world),
    );
    if (slugs.length === 0) return;
    worldsReady = await prefetchWorlds(slugs, {
      keepGoing: () => current === undefined && !prefetcher.preparing,
    });
    const line = document.querySelector('.waiting .worlds');
    if (line && worldsReady) line.textContent = 'Werelden klaar';
  } finally {
    prefetching = false;
  }
}

/** The lobby's lead: how far a lesson being prepared is (ADR-038), else when it begins. */
function waitingLead(): string {
  return prefetcher.preparing
    ? preparingLine(prefetcher.report)
    : 'De les begint vanzelf zodra de leerkracht hem start.';
}

/** The GPU's caps for the prefetch plan: the stage's renderer's, or in the lobby a probe's, once. */
let probedCaps: PanoCaps | undefined;
function prefetchCaps(): PanoCaps {
  if (stage) return rendererCaps(stage.renderer);
  // Only what was read is kept: a probe that failed is asked again at the next prepare.
  probedCaps ??= probeCaps();
  return probedCaps ?? NO_PANO_CAPS;
}

/**
 * A lesson's files fetched ahead (ADR-038, proposed): in the lobby while the teacher sets it
 * ready, and behind the lesson once it runs. One request at a time after a random wait, each read
 * to the end for the service worker to keep, checked against its size and hash, and dropped.
 */
const prefetcher = new Prefetcher({
  // A 360° photo as this headset will show it (ADR-041): a Quest fetches the cube, not the JPEG.
  plan: (lesson) =>
    assignmentPlan(lesson, {
      worldFiles: lessonWorldFiles(lesson.lesson, undefined, undefined, lesson.worlds),
      origin: location.origin,
      caps: prefetchCaps(),
    }),
  start: (plan, tuning, report) =>
    new PrefetchRunner(
      plan,
      {
        ...browserRunnerDeps(() =>
          session ? session.refreshMedia() : Promise.reject(new Error('no session')),
        ),
        report,
      },
      { tuning },
    ),
  send: (report) => session?.prefetchProgress(report),
  changed: () => {
    const lead = document.querySelector('.waiting .lead');
    if (lead) lead.textContent = waitingLead();
  },
});

/** What the session's prepare, end and release, and a revoked pairing, do here (ADR-038). */
const sessionFlow = new SessionFlow({
  prefetcher,
  running: () =>
    current
      ? { sessionId: assigned?.running === current ? assigned.assignment.sessionId : undefined }
      : undefined,
  evict: (lesson, media, variants) => void evictUnsigned(lesson, media, variants),
  showLobby: () => renderWaiting(),
  setFreeTime: (allowed) => {
    freeTimeAllowed = allowed;
  },
  endLesson: () => void endLesson(),
  leaveQuietly: () => void leaveLesson(),
  forgetName: () => session?.forgetName(),
  stopSession: () => {
    session?.stop();
    session = undefined;
  },
  forgetSchoolData: () => forgetSchoolData(),
});

/** The lessons a student may start alone, one step away from waiting. */
async function renderLessonList(): Promise<void> {
  overlay.innerHTML = '';
  const card = document.createElement('div');
  card.className = 'card';
  card.innerHTML = `
    <h1>Zelf oefenen</h1>
    <p class="hint">Kies een les. De leerkracht kan altijd een andere les starten.</p>
    <div class="lessons"></div>
    <p class="hint">Op een computer: sleep om rond te kijken, klik om te kiezen, pijltjestoetsen voor stappen.</p>
  `;
  const list = card.querySelector('.lessons')!;

  const back = document.createElement('button');
  back.className = 'quiet';
  back.type = 'button';
  back.textContent = 'Terug';
  back.addEventListener('click', () => renderWaiting());
  card.append(back);

  overlay.append(card);

  const identity = storedIdentity();
  // Only what a lesson document says is suitable. Everything else on this
  // headset is teacher-led material, and a child browsing alone should not
  // be able to start it.
  let lessons: LessonSummary[];
  try {
    lessons = (await listLessons(identity)).filter((entry) => entry.selfPractice);
  } catch (error) {
    // Unlinked meanwhile: the pairing screen is up, and no list goes over it.
    if (error instanceof DeviceRevokedError) return;
    throw error;
  }

  if (lessons.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'hint';
    empty.textContent = 'Er zijn nog geen lessen om zelf te oefenen.';
    list.append(empty);
  }

  for (const entry of lessons) {
    const button = document.createElement('button');
    button.className = 'lesson';
    button.type = 'button';
    const title = document.createElement('span');
    title.textContent = entry.title;
    const tag = document.createElement('span');
    tag.className = 'tag';
    tag.textContent = `${entry.mode} · ${entry.language}`;
    button.append(title, tag);
    button.addEventListener('click', () => {
      void start(entry);
    });
    list.append(button);
  }
}

function showError(message: string, detail?: string): void {
  overlay.innerHTML = '';
  const card = document.createElement('div');
  card.className = 'card';
  card.innerHTML = `<h1>Deze les kan niet starten</h1><p></p>`;
  card.querySelector('p')!.textContent = message;
  if (detail) {
    const pre = document.createElement('p');
    pre.className = 'hint';
    pre.textContent = detail;
    card.append(pre);
  }
  const back = document.createElement('button');
  back.className = 'lesson';
  back.type = 'button';
  back.textContent = '◀ Terug';
  back.addEventListener('click', showPicker);
  card.append(back);
  overlay.append(card);
}

let current:
  | {
      built: BuiltScene;
      ui: LessonUi;
      player: LessonPlayer;
      /** Places an AR lesson in front of the viewer. A no-op in VR. */
      arrive: () => void;
      /** The headset entered or left immersive mode: in VR is in the world (worldEntry). */
      presentingChanged: () => void;
      /** The class's (or the viewer's) guidance: text or voice (ADR-035). */
      setGuide: (guide: Guide) => void;
      /** The same lesson said again (a reconnect): its fresh signed URLs and cues, nothing restarted. */
      refresh: (
        media: Readonly<Record<string, string>>,
        cues: CueMap,
        variants: Readonly<PanoVariants>,
      ) => void;
      /** Its world and its posters' photos are in: the prefetcher may run beside it (ADR-038). */
      loaded: () => boolean;
      /** The class's (or the viewer's) Spannend: applies from the next step (ADR-043). */
      setSpannend: (on: boolean) => void;
      /** The teacher's pause, for the teacher's window onto the class: its models hold. */
      setPaused: (paused: boolean) => void;
      /** Sets the world's features to these states, animated (Bekijken's panel, ADR-058). */
      setWorldStates: (states: ReadonlyMap<string, string>) => void;
      /** What a lesson's picture needs of it (ADR-098, bootLessonPicture). */
      picture: LessonPicturing;
      dispose: () => void;
    }
  | undefined;

/** What a lesson's picture needs of the running lesson (ADR-098). */
type LessonPicturing = {
  /** Its world, photos and models in (each arrived or failed). */
  ready(): Promise<void>;
  /** How long step `index`'s changes take when it is entered forward by one. */
  changeSeconds(index: number): number;
  /**
   * What of the lesson did not load as the lesson has it, at step `index` (once `ready`): its world,
   * a world file, a poster's photo, a model, the step's 360° photo. Empty when the scene is whole.
   */
  problems(index: number): string[];
  /** Hides the names and the points for the picture; returns how to show them again. */
  hide(): () => void;
};

/**
 * One stage for the life of the app, not one per lesson.
 *
 * A WebXR session belongs to the canvas that started it, so building a new
 * renderer for the next lesson throws the student out of immersive mode --
 * twenty children all having to press "Start in VR" again between two lessons
 * a teacher meant to run back to back. The stage therefore outlives lessons,
 * and everything a lesson adds to it takes itself back off when it ends.
 *
 * Built on the first lesson rather than at startup: a headset sitting in the
 * lobby has no use for a WebGL context (the prefetch plan probes one briefly for
 * its 360° photo caps, render/pano-caps.ts `probeCaps`, and lets it go).
 */
let stage: Stage | undefined;

function theStage(): Stage {
  if (!stage) {
    stage = new Stage(document.body, desktop.eye);
    // The dev server's handle for looking round from a script (headless screenshots); never built in.
    if (import.meta.env.DEV) Object.assign(window, { __stage: stage });
    // The teacher's grid separates "the lesson is loaded" from "the student is
    // wearing it", and only the headset can tell the two apart.
    stage.onPresentingChange((presenting) => {
      session?.presenting(presenting);
      // The moment there is a head pose worth measuring from. An AR lesson
      // places itself in front of the student now rather than at the room's
      // origin, which is wherever the headset happened to be at check-in.
      if (presenting) current?.arrive();
      // Entering VR is entering the world: the ambience fades in, and the first voice follows.
      current?.presentingChanged();
      // Leaving immersive mode -- the headset's own exit -- ends a preview (ADR-058).
      if (!presenting && headsetMode.state === 'preview') finishBekijken();
    });
  }
  return stage;
}

// --- the live session ------------------------------------------------------

let session: SessionClient | undefined;
let linked = false;

/**
 * What this headset is called at its school, learned from the check-in.
 *
 * Undefined until the first one gets through, which is why the waiting screen
 * redraws when it arrives rather than holding everything up for it: a lesson
 * must be able to start in a room with no wifi.
 */
let deviceName: string | undefined;
/**
 * One of the platform's own test headsets (ADR-058), learned from the check-in: its lobby says so,
 * offers no practising and fetches no lesson worlds -- it only waits for previews. Memory only: the
 * next check-in says it again.
 */
let platformHeadset = false;
/** This boot's check-in has answered (ok or offline): the lobby may fetch for its lessons. */
let checkedIn = false;

/**
 * Whether the teacher has said this student may do something on their own.
 *
 * Set when the teacher lets them out of a lesson, cleared the moment another
 * lesson is pushed. Free time is something a teacher gives, not a menu that
 * is always there: a list of things to start in the two minutes before a
 * lesson is an invitation to wander off.
 */
let freeTimeAllowed = false;

/** The step the server last said the class is on, so we do not echo it back. */
let remoteStep: string | undefined;

/**
 * The lesson the teacher pushed, if any.
 *
 * Kept so a result recorded now can still be placed later: if the wifi is down
 * when a student answers and the teacher ends the class before it returns, the
 * flush needs to say which session the answer belongs to.
 */
let currentSessionId: string | undefined;

/**
 * The pushed lesson now running, and what it was pushed with. The hub says the
 * lesson again on every reconnect; the same one keeps running (sameAssignment).
 */
let assigned: { assignment: Assignment; running: NonNullable<typeof current> } | undefined;

/** Lobby, session or preview (ADR-058): a preview opens only from the lobby, and a lesson ends it. */
const headsetMode = new HeadsetMode();

/** Which preview's loading is the current one: a later one, a lesson or Sluiten stales it. */
const bekijkenStarts = new Generation();

/** Takes the showing preview's panel, points and idle timer away; undefined when none shows. */
let bekijkenUndo: (() => void) | undefined;

/** A class's lessons on their way in (startAssigned's awaits): no preview opens over one. */
let lessonsStarting = 0;

/** Said once on the lobby card: why a preview ended or could not open (ADR-058). */
let lobbyNotice: string | undefined;

function updateLinkStatus(): void {
  const element = document.querySelector('.link');
  if (!element) return;
  // Nothing when it is working. The heading already says what is happening,
  // and a second line repeating it is noise on a screen that thirty children
  // stare at for minutes at a time. Only trouble earns a sentence.
  element.textContent = linked
    ? ''
    : 'Geen verbinding met de server. De headset probeert het zelf opnieuw.';
}

/**
 * Connects to the live session.
 *
 * The headset never chooses a session: it connects and waits, and the teacher
 * pushes one (docs/05). Starting before the teacher has done anything is the
 * normal order of events in a classroom.
 */
function linkSession(): void {
  const identity = storedIdentity();
  if (!identity || session) return;

  session = new SessionClient({
    ticket: () => fetchTicket(identity),
    open: browserSocket,
    appVersion: __APP_VERSION__,
  });

  // The hub says this headset was unlinked (docs/05): unpaired at once, through the one place it
  // is done. A refused ticket has unpaired it already; saying it again changes nothing.
  session.onRevoked(() => {
    revoked(identity.deviceToken).catch((error: unknown) => {
      console.error('could not finish unpairing', error);
    });
  });

  session.onConnected((connected) => {
    linked = connected;
    updateLinkStatus();
    // Said again on every reconnect: the server's copy of this is per session
    // and a dropped socket may have outlived it.
    if (connected && stage) session?.presenting(stage.presenting);
    // The student's name is not re-sent here: the client remembers it and says
    // it again itself, on every connect *and* on every lesson pushed. Doing it
    // from this side covered only reconnects, which missed the case the name
    // is typed in -- on the waiting screen, before a session exists at all.
    // A reconnect is the moment to hand over anything that happened while the
    // headset was offline.
    if (connected) void flushResults();
  });

  session.onAssigned((assignment) => {
    // A lesson always wins (ADR-058): a preview showing ends here; startAssigned replaces it.
    if (headsetMode.sessionStarted()) endBekijken();
    // Said again on reconnecting (voice-guide design, H4): the lesson keeps
    // running -- a Wi-Fi blip neither starts it over nor restarts the voice.
    if (
      current &&
      assigned?.running === current &&
      sameAssignment(assigned.assignment, assignment)
    ) {
      resumeAssigned(assignment);
      return;
    }
    // A new lesson is the end of free time, whatever was happening before.
    freeTimeAllowed = false;
    lessonsStarting++;
    void startAssigned(assignment).finally(() => lessonsStarting--);
  });

  // A lesson the teacher is setting ready (ADR-038): the headset stays in the lobby and fills
  // its cache. Said again on every reconnect while it is being prepared.
  session.onPrepare((preparation) => {
    // A lesson always wins (ADR-058): out of a preview and back to the lobby, where the cache fills.
    if (headsetMode.sessionStarted()) {
      endBekijken();
      void leaveLesson();
    }
    sessionFlow.prepared(preparation);
  });

  session.onState((state) => applyLiveState(state));

  session.onEnded(() => {
    // During a preview there is no session here to end (ADR-058): the preview stays.
    if (!headsetMode.sessionEnded()) return;
    starts.leave();
    sessionFlow.ended();
  });

  // The teacher has let this student out. Same exit as the class ending: out
  // of VR, back to the lobby, free to pick something else or take it off --
  // which is exactly the moment free time begins.
  session.onReleased(() => {
    if (!headsetMode.sessionEnded()) return;
    sessionFlow.released();
  });

  // Open in Quest (ADR-058): a world or object sent from the console, shown from the lobby only.
  session.onPreview((open) => void openBekijken(open));
  session.start();
}

async function flushResults(): Promise<void> {
  if (!session) return;
  await resultQueue.flush(async (batch) => {
    const outcomes = await session!.sendResults(
      batch.map((entry) => ({
        stepId: entry.stepId,
        kind: entry.kind,
        payload: entry.payload,
        at: entry.at,
        sessionId: entry.sessionId,
        resultId: entry.resultId,
      })),
    );
    // Stored, or dropped for good, is answered: let go of it. `kept` -- no
    // answer in time, or the server could not store it just now -- is sent
    // again on the next flush, and stored once however often it is sent.
    return batch.flatMap((entry, index) => {
      const outcome = outcomes[index];
      if (outcome === 'dropped') {
        console.warn(`result for step ${entry.stepId} refused by the server; not sent again`);
      }
      return outcome === undefined || outcome === 'kept' ? [] : [entry.resultId];
    });
  });
}

/**
 * Open in Quest (ADR-058): a world or object sent from the console, or (a platform test headset, from
 * Steekproef) a school's lesson. Only from the lobby with nothing running here; the ticket is redeemed with this headset's own token, then the item is played as a
 * one-step lesson under a bare lectern with the Bekijken panel and the teleport points.
 */
async function openBekijken(open: PreviewOpen): Promise<void> {
  const identity = storedIdentity();
  const busyHere = current !== undefined || lessonsStarting > 0 || prefetcher.preparing;
  if (!identity || !headsetMode.previewOffered(busyHere)) return;
  const mine = bekijkenStarts.begin();
  showWaiting(BEKIJKEN_TEXT.loading(open.title));
  const outcome = await fetchQuestPreview(open.ticket, (path, init) =>
    deviceFetch(identity, path, init),
  );
  if (!bekijkenStarts.current(mine)) return;
  if (outcome.kind === 'revoked') {
    // Unpaired by the request that heard it (unpairHere); the pairing screen is up.
    headsetMode.reset();
    return;
  }
  if (outcome.kind === 'gone' || outcome.kind === 'failed') {
    finishBekijken(
      outcome.kind === 'gone' ? BEKIJKEN_TEXT.gone : `${BEKIJKEN_TEXT.failed} (${outcome.detail})`,
    );
    return;
  }
  try {
    await (outcome.kind === 'lesson'
      ? showLessonPreview(outcome.loaded, mine)
      : showBekijken(outcome.answer, mine));
  } catch (error) {
    // Whatever goes wrong building it (a model no place keeps the distance rule for, a panel that
    // will not hang): the lobby, with the reason, never a loading line for ever.
    console.error('Bekijken could not start', error);
    if (bekijkenStarts.current(mine)) {
      finishBekijken(`${BEKIJKEN_TEXT.failed} (${(error as Error).message})`);
    }
  }
}

/** Builds the preview's world (a package checked as a lesson's is), runs it and hangs the panel on it. */
async function showBekijken(answer: QuestPreviewAnswer, mine: number): Promise<void> {
  const plan = bekijkenPlan(answer);
  if (!plan) {
    finishBekijken(BEKIJKEN_TEXT.failed);
    return;
  }
  const prepared = plan.world
    ? await prepareWorld(plan.world.slug, answer.worlds, answer.media, fetchBytes)
    : undefined;
  if (!bekijkenStarts.current(mine)) return;
  const problem = prepared?.problems[0];
  if (problem) {
    // The existing words for a package the headset refuses, then the lobby.
    console.warn(`wereldpakket ongeldig: ${problem.path} ${problem.message}`, prepared?.problems);
    finishBekijken(`Wereldpakket: ${problem.message}`);
    return;
  }
  overlay.innerHTML = '';
  current?.dispose();
  current = undefined;
  try {
    current = run(plan.lesson, {
      recorded: false,
      bare: true,
      media: answer.media,
      variants: answer.variants,
      cues: {},
      models: answer.models,
      ...(plan.world ? { world: prepared?.source ?? null } : {}),
    });
  } catch (error) {
    // A world or model the renderer chokes on: said on the lobby card, as a refused package is.
    console.error('Bekijken could not start', error);
    finishBekijken(`${BEKIJKEN_TEXT.failed} (${(error as Error).message})`);
    return;
  }
  setLessonRunning(true);
  const now = current;
  const idle = new IdleTimer(BEKIJKEN_IDLE_MS, () => finishBekijken());
  const touch = (): void => idle.touch();
  const take = hangBekijken({
    target: now,
    stage: theStage(),
    plan,
    title: answer.item.name,
    onClose: () => finishBekijken(),
    onInput: touch,
  });
  // The desktop preview's mouse and keys count as input too.
  window.addEventListener('pointerdown', touch);
  window.addEventListener('keydown', touch);
  idle.touch();
  bekijkenUndo = () => {
    idle.stop();
    window.removeEventListener('pointerdown', touch);
    window.removeEventListener('keydown', touch);
    take();
  };
}

/**
 * Steekproef's Open in Quest (ADR-058 amendment): a school's lesson played solo as its headsets play
 * it, recorded nowhere, with the "Voorbeeld — telt niet mee" marker and Sluiten on the lectern.
 */
async function showLessonPreview(loaded: LoadedLesson, mine: number): Promise<void> {
  const world = await chooseWorld(loaded.lesson, loaded.worlds, loaded.media);
  if (!bekijkenStarts.current(mine)) return;
  overlay.innerHTML = '';
  current?.dispose();
  current = undefined;
  // Its sounds and voice on, as a lesson practised alone has them: no class's switches here.
  audio?.setSession({ ambient: true, voice: true, paused: false });
  const started = playLessonPreview({
    loaded,
    run: (lesson, options) => {
      current = run(lesson, { ...options, ...world, guide: localGuide });
      return current;
    },
    hangMarker: (target, onClose) => {
      const marker = new PreviewMarker(
        loaded.lesson.mode,
        onClose,
        loaded.lesson.mode === 'vr' ? lecternPanelTop(target.ui.group) : undefined,
      );
      target.ui.group.add(marker.group);
      target.ui.setExtraButtons(marker.pressable);
      return () => {
        target.ui.setExtraButtons([]);
        marker.dispose();
      };
    },
    onClose: (notice) => finishBekijken(notice),
    listen: (touch) => {
      // The desktop preview's mouse and keys count as input too.
      window.addEventListener('pointerdown', touch);
      window.addEventListener('keydown', touch);
      return () => {
        window.removeEventListener('pointerdown', touch);
        window.removeEventListener('keydown', touch);
      };
    },
  });
  setLessonRunning(true);
  bekijkenUndo = started.undo;
}

/** Takes a showing preview's panel, points and timer away, and stales one still loading. */
function endBekijken(): void {
  bekijkenStarts.leave();
  bekijkenUndo?.();
  bekijkenUndo = undefined;
}

/** Sluiten, the headset's exit, the idle timer, or a preview that could not open: back to the lobby. */
function finishBekijken(notice?: string): void {
  if (headsetMode.state !== 'preview') return;
  endBekijken();
  headsetMode.previewClosed();
  lobbyNotice = notice;
  void leaveLesson();
}

/**
 * The lesson taken down on this headset: its things off the stage, the lobby behind it, out of
 * immersive mode. Nothing said to the session -- endLesson says the rest, and a lesson left for a
 * session being prepared (ADR-038) must not tell that one it has left.
 */
async function leaveLesson(): Promise<void> {
  starts.leave();
  remoteStep = undefined;
  // currentSessionId is deliberately left alone: anything still queued belongs
  // to the lesson that has just finished and has to say so when it is finally
  // flushed. It is replaced when the next lesson is pushed.

  // The lesson's own things go first, so the lobby is already behind whatever
  // the student is looking at by the time the headset lets go.
  current?.dispose();
  current = undefined;
  hud.textContent = '';
  showPicker();

  // Then leave immersive mode -- and only hide the canvas afterwards. A canvas
  // with `display: none` cannot present, so hiding it while the session is
  // still running freezes the view in the headset instead of returning it,
  // leaving the student to close the session by hand.
  await stage?.exitXr();
  if (stage) stage.visible = false;
}

/**
 * The teacher has stopped the class.
 *
 * Leaving immersive mode is the part that matters, and it only matters in a
 * headset: the lobby is DOM, and DOM cannot be seen from inside a WebXR
 * session. Without this the student is left in a session with nothing
 * rendering in it -- which on a laptop looks like it works, because there is
 * no session to be stuck in.
 */
async function endLesson(): Promise<void> {
  await leaveLesson();

  // Last, once it is true. The teacher's grid distinguishes "may stop" from
  // "has stopped", and only this headset knows which it is.
  session?.left();

  // And whoever was wearing it has finished. The next lesson asks again from
  // an empty field, because the next child is quite possibly a different one
  // and a headset should not put a stranger's name on their tile.
  session?.forgetName();
}

/**
 * Whether a `session.state` has come since the teacher pushed this lesson:
 * until one has, the teacher's switches are not known, and "Voorlezen" waits
 * rather than flashing on for a class that has it off.
 */
let classStateSeen = false;

/**
 * The class's shared time (ADR-054, 20 Oct 2026): set by every `session.state`, kept to by a model
 * going round, so every headset in a class shows it at the same point of its lap.
 */
const classClock = new ClassClock();

/** Which start of a pushed lesson is the current one (engine/generation.ts). */
const starts = new Generation();

/** A lesson the teacher pushed. The headset did not ask for it. */
async function startAssigned(assignment: Assignment): Promise<void> {
  const ticket = starts.begin();
  // The pairing this lesson was pushed under (see below).
  const pairedAs = storedIdentity()?.deviceToken;
  overlay.innerHTML = '';
  remoteStep = undefined;
  currentSessionId = assignment.sessionId;
  classStateSeen = false;
  classClock.reset();
  // Calm until this class's first session.state says otherwise (ADR-043).
  classSpannend = false;

  // A VR session cannot show an AR lesson, so a change of mode has to leave
  // immersive mode. Same mode keeps the session, which is the whole point of
  // sharing the stage: a second lesson arrives without the class having to
  // put their headsets back into VR.
  const wanted: XRSessionMode = assignment.lesson.mode === 'ar' ? 'immersive-ar' : 'immersive-vr';
  if (stage && stage.sessionMode !== undefined && stage.sessionMode !== wanted) {
    await stage.exitXr();
    // Unlinked while leaving VR -- or paired to another school since: this school's lesson does
    // not start over the pairing screen, nor under the next school.
    if (storedIdentity()?.deviceToken !== pairedAs || !starts.current(ticket)) return;
  }

  void evictUnsigned(assignment.lesson, assignment.media, assignment.variants);
  const choice = await chooseWorld(assignment.lesson, assignment.worlds, assignment.media);
  // The class ended, or another lesson was pushed, while its world was made ready: nothing starts,
  // and the session is told nothing (no ready() for a stale session).
  if (!starts.current(ticket) || storedIdentity()?.deviceToken !== pairedAs) return;
  current?.dispose();
  // Text until the first session.state says what the teacher chose (ADR-035).
  current = run(assignment.lesson, {
    media: assignment.media,
    variants: assignment.variants,
    cues: assignment.cues,
    ...(assignment.models ? { models: assignment.models } : {}),
    ...choice,
    inClass: true,
    // The class's meubelset, and the server that decides who holds what (ADR-037).
    ...(session && isFurnishLesson(assignment.lesson)
      ? { furniture: { set: assignment.furniture, link: sessionLink(session) } }
      : {}),
  });
  assigned = { assignment, running: current };
  setLessonRunning(true);

  // Locked until the first session.state says otherwise, which arrives within
  // the same breath. Guessing 'free' would flash a Verder button onto the
  // lectern and then take it away again.
  current.player.setControl('locked');

  // Tells the teacher's grid this headset is loaded and waiting.
  session?.ready();
  followWithPrefetch(assignment);
  await flushResults();
}

/**
 * The prefetch behind a running lesson (ADR-038): the prepared lesson's runner, or a new one after
 * plain Start, at one request, leaving the step the lesson is loading to the lesson, and waiting
 * until its world and posters are in.
 */
function followWithPrefetch(assignment: Assignment): void {
  const running = current;
  if (!running) return;
  prefetcher.assigned(assignment, {
    stepIndex: running.player.state.index,
    mayRun: () => current === running && running.loaded(),
  });
}

/**
 * The lesson already running, said again after a reconnect: it keeps its step,
 * its voice and its place. Only what is new is taken -- the media's fresh
 * signatures and the cues -- and the class's step, guide, sound, pause and
 * lock follow in the session.state the hub sends next, through the paths they
 * always take (a step already shown, a guide already set: nothing happens).
 */
function resumeAssigned(assignment: Assignment): void {
  if (!current) return;
  assigned = { assignment, running: current };
  void evictUnsigned(assignment.lesson, assignment.media, assignment.variants);
  current.refresh(assignment.media, assignment.cues, assignment.variants);
  // Moving freely, this headset may have gone on while offline, and what it
  // said then went nowhere; a locked class is put back on its step by the hub.
  if (current.player.control === 'free') session?.progress(current.player.state.step.id);
  session?.ready();
  // Its prefetch keeps going (or starts again, with these fresh signatures, if it gave up).
  followWithPrefetch(assignment);
}

/**
 * Follows the class.
 *
 * The API owns the state and this renders what it receives (ADR-007), so a
 * step it did not expect is not an error: it is where the teacher has taken
 * everyone.
 */
function applyLiveState(state: LiveState): void {
  const stepId = state.stepId;
  // Known from now: told before the engine tells whoever follows it.
  classStateSeen = true;
  // Before the step changes below: a model going round in it keeps to the class's time.
  classClock.hear(state, performance.now());
  // The teacher's two switches, and the pause's duck (ADR-034): for every headset, lesson or not.
  audio?.setSession({ ...soundOf(state), paused: state.paused === true });
  // The teacher's Spannend (ADR-043): read by the lesson at its next step entry.
  classSpannend = spannendOf(state);
  current?.setSpannend(classSpannend);
  if (!current) return;

  if (followsClass(state) && typeof stepId === 'string') {
    // Set before anything can notify, so the change handler does not mistake
    // the server's own step for something this headset did and report it back.
    remoteStep = stepId;
    if (current.player.state.step.id !== stepId) current.player.goto(stepId);
  } else {
    // In a free class the step in this message is where the *class* is, not
    // where this student is. Following it would drag them back the instant
    // they moved themselves.
    remoteStep = undefined;
  }

  current.player.setControl(controlFor(state));
  // The teacher's "Begeleiding" (ADR-035): anything but voice, an older API's absence too, is text.
  current.setGuide(guideOfState(state));
}

async function start(summary: LessonSummary): Promise<void> {
  overlay.innerHTML = '';
  hud.textContent = 'Laden…';
  // The pairing this lesson is loaded under: unlinked while it loads, it does not start.
  const pairedAs = storedIdentity()?.deviceToken;

  let lesson: Lesson;
  let media: Readonly<Record<string, string>>;
  let cues: CueMap;
  let variants: PanoVariants;
  let models: ModelCatalogue;
  let worlds: Readonly<Record<string, ServedWorld>>;
  try {
    ({ lesson, media, cues, variants, models, worlds } = await loadLesson(
      summary,
      storedIdentity(),
    ));
  } catch (error) {
    hud.textContent = '';
    // Unlinked: unpaired already, the pairing screen up. Not a lesson that failed.
    if (error instanceof DeviceRevokedError) return;
    if (storedIdentity()?.deviceToken !== pairedAs) return;
    // Carried to the next check-in that gets through: from a console this
    // looks exactly like a headset nobody has used, and it is not.
    rememberTrouble('lesson-failed', (error as Error).name);
    showError('De les kon niet worden geladen.', (error as Error).message);
    return;
  }

  // Unlinked (or paired elsewhere) while it loaded: its answer came in just before; it does not
  // start over the pairing screen.
  if (storedIdentity()?.deviceToken !== pairedAs) {
    hud.textContent = '';
    return;
  }

  // Practising alone, not in a class: results from here belong to no session,
  // and must not be filed against whichever lesson the teacher last pushed.
  currentSessionId = undefined;
  // Nor are its sound switches a class's.
  audio?.setSession({ ambient: true, voice: true, paused: false });

  // A photo the API no longer signs leaves this headset now (media-cache.ts).
  void evictUnsigned(lesson, media, variants);

  const choice = await chooseWorld(lesson, worlds, media);
  // Unlinked while its world was fetched: as above.
  if (storedIdentity()?.deviceToken !== pairedAs) {
    hud.textContent = '';
    return;
  }
  current?.dispose();
  current = run(lesson, { media, variants, cues, models, guide: localGuide, ...choice });
  setLessonRunning(true);
}

/** A world lesson's world source: what it was given (`null`: none could be had), else the code world's. */
function worldSourceOf(
  lesson: Lesson,
  given: WorldSource | null | undefined,
): WorldSource | undefined {
  if (lesson.scene.type !== 'world') return undefined;
  return given === undefined ? worldSourceFor(lesson.scene.world) : (given ?? undefined);
}

/**
 * A world lesson's world, before it runs (ADR-049, W11): the served package of its slug, checked and
 * its height map fetched, else the code world. A package that cannot be shown whole gives `null` --
 * the lesson shows the studio -- and a note for the desktop preview; a headset says nothing of it.
 */
async function chooseWorld(
  lesson: Lesson,
  worlds: Readonly<Record<string, ServedWorld>>,
  media: Readonly<Record<string, string>>,
): Promise<{ world?: WorldSource | null; worldNote?: string }> {
  // `?world=<slug>` tries a VR lesson in another world (run).
  const slug =
    desktop.world && lesson.mode === 'vr'
      ? desktop.world
      : lesson.scene.type === 'world'
        ? lesson.scene.world
        : undefined;
  if (!slug) return {};
  const prepared = await prepareWorld(slug, worlds, media, fetchBytes);
  const problem = prepared.problems[0];
  if (problem) {
    console.warn(`wereldpakket ongeldig: ${problem.path} ${problem.message}`, prepared.problems);
    return { world: null, worldNote: `Wereldpakket: ${problem.message}` };
  }
  if (prepared.source) return { world: prepared.source };
  // No package and (for a taken-over slug) no code world either: the studio, said on the desktop.
  const note = takenOverNote(slug);
  return note ? { world: null, worldNote: note } : { world: null };
}

type RunOptions = {
  /**
   * False for the teacher's window onto the class: it renders the lesson and
   * nothing else -- no pointer, no hotspots to open, no way into a headset.
   */
  interactive?: boolean;
  /**
   * False for a lesson played from a preview ticket (ADR-029): it belongs to
   * no session and no headset, so an answer goes nowhere and nobody is asked
   * their name.
   */
  recorded?: boolean;
  /** Offered as "Klaar ✓" on the last step, in place of asking the teacher. */
  onFinish?: () => void;
  /** Media id -> signed URL, as the lesson came with it (posters' photos, voice-overs). */
  media?: Readonly<Record<string, string>>;
  /** Media id -> a 360° photo's signed cube and light copy (ADR-041). */
  variants?: Readonly<PanoVariants>;
  /** A lesson the teacher pushed: its switches are unknown until the first session.state. */
  inClass?: boolean;
  /** The pupil's Voorlezen on from the start: the demo only (voiceStartsOn). */
  voiceOn?: boolean;
  /** Media id -> subtitle cues for its speech (ADR-035). */
  cues?: CueMap;
  /** The guidance to start with: the viewer's outside a class; a class starts with text. */
  guide?: Guide;
  /**
   * A villa lesson's furniture (ADR-037): the meubelset and where its holds go -- the session in a
   * class; outside one a LocalFurnishing, which grants every grab and keeps nothing.
   */
  furniture?: { set: FurnitureAssigned | undefined; link?: FurnishingLink };
  /** The library models the lesson names, as served (ADR-043, contract C4). */
  models?: ModelCatalogue;
  /**
   * Where the lesson's world comes from (chooseWorld): absent, the code world of its slug; `null`,
   * none could be had, so the lesson shows the studio.
   */
  world?: WorldSource | null;
  /** Said in the corner of a desktop preview when a served package was refused. */
  worldNote?: string;
  /**
   * The lesson plays itself to its end (engine/autoplay.ts): a demo built with `--autoplay` only,
   * through `virtual:demo-play`; nothing else sets it.
   */
  autoplay?: boolean;
  /**
   * Bekijken mode (ADR-058): the lectern is placed as always but shows no panel, words or buttons;
   * main.ts hangs the Bekijken panel on it.
   */
  bare?: boolean;
};

function run(asked: Lesson, options: RunOptions = {}): NonNullable<typeof current> {
  // `?world=<slug>`: a VR lesson tried in another world (desktop preview).
  const lesson: Lesson =
    desktop.world && asked.mode === 'vr'
      ? { ...asked, scene: { type: 'world', world: desktop.world } }
      : asked;
  const interactive = options.interactive !== false;
  const recorded = options.recorded !== false;
  const stage = theStage();
  stage.resetForNextLesson();
  stage.visible = true;
  const isAr = lesson.mode === 'ar';

  if (isAr) {
    // Desktop preview only: a light room so the demo furniture reads as a
    // room. MeasureTool clears this the moment a real AR session starts, so
    // passthrough is never painted over.
    stage.scene.background = new THREE.Color(0xdfe6ee);
    stage.setDesktopPitch(-0.25);
  } else {
    // The studio's own horizon (ADR-030), so nothing shows through it as a
    // dark seam; space is black.
    stage.scene.background = new THREE.Color(
      lesson.scene.type === 'studio' ||
        lesson.scene.type === 'world' ||
        lesson.scene.type === 'pano360'
        ? STUDIO_HORIZON
        : 0x01020a,
    );
  }

  // A world lesson's world: the served package of its slug, else the code world; with neither (a
  // package refused whole, W11) the lesson shows the studio, as `?look=flat` does.
  const source = worldSourceOf(lesson, options.world);
  // Every lesson-time question about the world (its features, states, sound) is answered from this
  // world source: the package's own JSON, or the code world's catalogue entry.
  const worlds = catalogueOfSource(lesson.scene.type === 'world' ? lesson.scene.world : '', source);
  const flat = desktop.look === 'flat' || (lesson.scene.type === 'world' && !source);
  const built = buildScene(lesson, {
    media: options.media ?? {},
    worlds,
    look: flat ? 'flat' : desktop.look,
    ...(options.models ? { models: options.models } : {}),
  });
  stage.scene.add(built.root);

  // The look (ADR-032): shadow, sky light, haze, halos -- and a world, when
  // the lesson takes place in one. A VR lesson starts black and fades in
  // over 1.5 s once its world is in, or after 15 s whatever is missing.
  // A 360° photo (ADR-036): the note that the studio stands in for one waits
  // for the notes below, which the photo's view may need before they exist.
  const isPano = !isAr && isPanoLesson(lesson);
  // Kept as its own copies: a reconnect's fresh signatures are put in (refresh),
  // where a 360° photo still to come reads them too.
  const media: Record<string, string> = { ...(options.media ?? {}) };
  const variants: PanoVariants = { ...(options.variants ?? {}) };
  let panoNoteText: string | undefined;
  let notePano = (text: string | undefined): void => {
    panoNoteText = text;
  };
  const look = applyLook(
    {
      scene: stage.scene,
      camera: stage.camera,
      renderer: stage.renderer,
      activeCamera: () => stage.activeCamera,
      redrawShadow: () => stage.redrawShadow(),
    },
    lesson,
    built,
    {
      flat,
      ...(source
        ? {
            world: source.definition,
            manifest: source.manifest,
            urlOf: source.urlOf,
            fetchers: browserFetchers(fixtureFiles),
          }
        : {}),
      ...(isPano ? { pano: { media, variants, onNote: (text) => notePano(text) } } : {}),
    },
  );
  // The world's sound (ADR-034): a VR world lesson only -- the studio, the
  // solar system and AR stay silent -- and never in a teacher's window onto
  // the class (no engine there). Comes up with the picture, goes with the lesson.
  // Said to whoever is previewing, and in no headset: a DOM note is never
  // drawn in an immersive session, and a class would only see plainer shapes.
  // With the numbers, so whoever wrote the lesson knows how far over it is.
  // Photos that did not load join it when they have all been tried.
  const notes: string[] = [];
  const shapeNoteText = interactive ? shapeNote(built.shapes) : undefined;
  if (shapeNoteText) notes.push(shapeNoteText);
  if (interactive && options.worldNote) notes.push(options.worldNote);
  let removeNotes = notes.length > 0 ? cornerNotice(notes.join(' · '), 'left') : (): void => {};
  /** A counted note ("Geluid: …", "Voorlezen: …") in place of its last count; `undefined` takes it away. */
  const putNote = (kind: string, text: string | undefined): void => {
    if (!running) return;
    const at = notes.findIndex((note) => note.startsWith(kind));
    if (at >= 0) notes.splice(at, 1);
    if (text) notes.splice(at >= 0 ? at : notes.length, 0, text);
    removeNotes();
    removeNotes = notes.length > 0 ? cornerNotice(notes.join(' · '), 'left') : (): void => {};
  };

  const plan = interactive ? soundPlanFor(lesson, source) : undefined;
  const soundMissing = { unloaded: 0, unplayable: 0 };
  const soundscape =
    plan && audio && lesson.scene.type === 'world'
      ? audio.play({
          world: lesson.scene.world,
          worlds,
          plan,
          ...(source ? { manifest: source.manifest, urlOf: source.urlOf } : {}),
          fetchFile: fetchBytes,
          onMissing: (id, reason) => {
            console.warn(
              `world sound ${id} could not be ${reason === 'fetch' ? 'loaded' : 'decoded'}`,
            );
            // Said to whoever previews: not loaded, or not playable here (Safari
            // may not decode Ogg Opus); one note, counted.
            if (reason === 'fetch') soundMissing.unloaded++;
            else soundMissing.unplayable++;
            putNote('Geluid:', missingNote(soundMissing));
          },
        })
      : undefined;
  if (!soundscape) audio?.stop();
  const setWorldStates = (states: ReadonlyMap<string, string>, animate: boolean): void => {
    look.setWorldStates(states, animate);
    soundscape?.setStates(states, animate);
  };
  const moving = Object.values(lesson.objects ?? {}).some(
    (object) => (object.spin ?? 0) !== 0 || object.orbit !== undefined,
  );
  const engine = audio;
  // A 360° photo lesson's HUD says what the GPU takes and what the photo went up as (ADR-041).
  const panoCaps = isPano ? rendererCaps(stage.renderer) : undefined;
  if (desktop.perf && panoCaps) console.info(`360° photos: ${capsLine(panoCaps)}`);
  const perf = desktop.perf
    ? new PerfMeter(
        stage.camera,
        stage.renderer,
        engine && plan ? () => engine.stats() : undefined,
        panoCaps ? () => panoHudLines(panoCaps) : undefined,
      )
    : undefined;
  let running = true;
  // "360°-foto niet geladen: de les staat in de studio", or "… nog niet gekozen".
  if (interactive) {
    // Said while the studio stands in, taken back when a photo shows.
    notePano = (text) => putNote('360°-foto', text);
    if (panoNoteText) notePano(panoNoteText);
  }

  // Entering the world (Pieter, 29 Sep 2026; engine/guide.ts, worldEntry).
  // In a headset the "Start in VR" tap lets the sound play, but the ambience
  // fades in only once the XR session has started, and the first voice 2 s
  // after that. In a browser the ambience starts when the scene has loaded
  // (the page tries to let the sound play then; without a click it stays
  // silent, and the first click or key does it, as ever), and the first
  // voice 2 s after the later of the two.
  let sceneLoaded = false;
  /** The posters' photos have arrived or failed: with the world, what the lesson loads at its start. */
  let photosSettled = false;
  void built.photos.then(
    () => (photosSettled = true),
    () => (photosSettled = true),
  );
  /** This browser can enter the lesson's XR mode: a headset. Undefined until asked. */
  let headset: boolean | undefined;
  let ambientStarted = false;
  let voiceEntered = false;
  const checkEntry = (): void => {
    if (!running) return;
    const entry = worldEntry({
      sceneLoaded,
      headset,
      presenting: stage.presenting,
      unlocked: engine?.unlocked ?? true,
    });
    if (entry.ambient && !ambientStarted) {
      ambientStarted = true;
      soundscape?.start();
    }
    if (entry.voice && !voiceEntered) {
      voiceEntered = true;
      guideLog(`in the world: the first voice in ${WORLD_ENTRY_VOICE_SECONDS} s`);
      voice?.enter(WORLD_ENTRY_VOICE_SECONDS);
      // Its panel already showing: watched from the entry's longer wait (ADR-035, decision 11).
      if (shownEntry === entryCount) {
        armStall(entryCount, WORLD_ENTRY_VOICE_SECONDS + GUIDE_STALL_SECONDS);
      }
    }
  };
  /** In a browser, once the scene is in: try to let the sound play (it usually needs a click). */
  const tryResume = (): void => {
    if (running && sceneLoaded && headset === false && engine && !engine.unlocked) {
      engine.tryStart();
    }
  };
  const stopWaitingForSound = engine?.onUnlocked(() => checkEntry()) ?? ((): void => {});
  if (interactive) {
    void (
      navigator.xr?.isSessionSupported(isAr ? 'immersive-ar' : 'immersive-vr') ??
      Promise.resolve(false)
    ).then(
      (supported) => {
        headset = supported;
        tryResume();
        checkEntry();
      },
      () => {
        headset = false;
        tryResume();
        checkEntry();
      },
    );
  }

  if (!isAr) stage.holdBlack(isPano ? PANO_HINT : undefined);
  void look.ready.then(() => {
    if (!running) return;
    if (!isAr) stage.fadeIn();
    sceneLoaded = true;
    tryResume();
    checkEntry();
    // Every step's voice-over, for the service worker to keep: after the
    // world, which is bigger and needed first.
    voice?.prefetch(voicesOf(lesson.steps, media));
    const missing = look.missing();
    if (interactive && missing > 0) {
      notes.push(
        `Wereld: ${missing} ${missing === 1 ? 'bestand' : 'bestanden'} niet geladen (pnpm worlds:fetch)`,
      );
      removeNotes();
      removeNotes = cornerNotice(notes.join(' · '), 'left');
    }
  });

  let noted = true;
  /** Posters' photos that did not load: a lesson's picture is refused while any is (ADR-098). */
  let photosFailed = 0;
  void built.photos.then(
    ({ failed }) => (photosFailed = failed),
    () => (photosFailed = 1),
  );
  void built.photos.then(({ failed }) => {
    const text = interactive ? photoNote(failed) : undefined;
    if (!text || !noted) return;
    notes.push(text);
    removeNotes();
    removeNotes = cornerNotice(notes.join(' · '), 'left');
  });
  const removeShapeNote = (): void => {
    noted = false;
    removeNotes();
  };

  // Measured now, while the lesson still stands at the origin: `arrive`
  // below moves it, and the padlock lives in its own space.
  const lockAt = lockAnchor(boundsOf(built));

  // Reused every frame rather than rebuilt: this runs at 72 Hz with two rays.
  const pointer = new THREE.Raycaster();
  const pickableBodies = [...built.objects.values()].map((object) => object.body);

  // An AR lesson is placed in a real room. Left at the origin it stands
  // inside whoever started the session, because that is where the headset
  // was at the time -- so it arrives in front of them and is theirs to move
  // and resize from there. A VR lesson is not placeable: there the scene is
  // the room, and a solar system a student could shrink would be a toy
  // rather than a place to stand.
  const placeable = isAr
    ? new Placeable(built.root, stage, {
        // The skeleton's own outline while it is being built, so the tray in
        // front of it -- which lives in its space -- is not a handle for the
        // whole body.
        bounds: () => {
          for (const bench of benches.values()) return bench.skeletonBox();
          return new THREE.Box3().setFromObject(built.root);
        },
      })
    : undefined;
  // Straight away as well as on entering the headset: a lesson pushed while a
  // student is already in AR never sees presenting change, and the laptop
  // preview never presents at all -- both would otherwise open inside the
  // skeleton.
  placeable?.arrive();

  const player = new LessonPlayer(lesson, {
    onResult: (result) => {
      // A played preview keeps nothing: not in this browser's queue, where a
      // paired laptop would later send it to a class it was never in.
      if (!recorded) return;
      // Written down before anything else touches it: a headset that loses
      // Wi-Fi, or a student who takes it off, must not lose the answer. The
      // flush keeps it until the server acknowledges it. Practising alone
      // (no session) keeps nothing: the queue would hand it to the next class.
      resultQueue.add(lesson.id, result, currentSessionId);
      void flushResults();
    },
  });

  const ui = new LessonUi(stage, lesson, player, worlds);
  if (options.bare) ui.setBare(true);

  // "Voorlezen" (ADR-033): a step's voice-over on the voice bus, when the
  // pupil asks -- the button in the panel's corner, or V. Not in a teacher's
  // window onto the class (no engine there). Shown while the class's
  // Voorlezen is on and not paused, once a session.state has said so; a
  // lesson practised alone or played from a ticket has no class, so it is on
  // there, and the viewer's mute silences it with the rest.
  // (`media`, above, is the lesson's own copy: a reconnect's fresh signatures are put in.)

  // Voice as guide (ADR-035): the class's "Begeleiding" -- text until a
  // session.state says otherwise -- or, outside a class, `?guide=`, the demo's
  // build, the start card and G. Never in a teacher's window onto the class.
  const cues: Record<string, readonly Cue[]> = { ...(options.cues ?? {}) };
  const inClass = options.inClass === true;
  let guide: Guide = interactive ? (options.guide ?? 'text') : 'text';
  /** How this step is guided now (engine/guide.ts, guideFor). */
  let render: GuideRender = 'text';
  /** This step's voice was due over 3 s ago and has not begun: the text box for the rest of it. */
  let stalled = false;
  /** Bumped by every step change: what was timed for an older one does nothing. */
  let entryCount = 0;
  /** The entry whose panel has shown (after the teleport's fade). */
  let shownEntry = 0;
  const clock = (): number => performance.now() / 1000;
  const later = (run: () => void, seconds: number): (() => void) => {
    const timer = window.setTimeout(run, seconds * 1000);
    return () => window.clearTimeout(timer);
  };
  /** The guide's timeline, for whoever previews (and the Quest's console). */
  const guideLog = (what: string): void => {
    if (interactive) console.debug(`guide ${clock().toFixed(2)} s: ${what}`);
  };
  const pacer = new GuidePacer({
    now: clock,
    schedule: later,
    advance: () => {
      guideLog('moves on by itself');
      player.next();
    },
  });

  /** A demo built with `--autoplay`: it answers, moves on and ends by itself (engine/autoplay.ts). */
  const autoplay =
    interactive && options.autoplay === true
      ? new Autoplay({ now: clock, schedule: later, act: (move) => autoplayAct(move) })
      : undefined;

  const voice =
    interactive && engine
      ? new VoicePlayer(engine, {
          media,
          fetchFile: fetchBytes,
          onChange: () => voiceChanged(),
          on: options.voiceOn === true,
          mode: guide === 'voice' ? 'guide' : 'button',
          cues,
          schedule: later,
          // Nothing starts by itself until the pupil is in the world (checkEntry).
          held: true,
          onFinished: (stepId) => heard(stepId),
          onEnded: (stepId) => voiceEnded(stepId),
          onMissing: (missing) => {
            // Said to whoever previews, and taken back once a file is had after all.
            if (missing.unloaded + missing.unplayable > 0) {
              console.warn(
                `voice-overs: ${missing.unloaded} not loaded, ${missing.unplayable} not decodable`,
              );
            }
            putNote(
              'Voorlezen:',
              missing.unloaded + missing.unplayable > 0 ? voiceNote(missing) : undefined,
            );
          },
        })
      : undefined;
  const voiceShown = (): boolean =>
    voice !== undefined &&
    engine !== undefined &&
    voiceButtonShown({
      available: voice.available,
      voice: classVoice({
        inClass: options.inClass === true,
        stateSeen: classStateSeen,
        voice: engine.input.voice,
      }),
      paused: engine.input.paused,
    });
  const showVoice = (): void => {
    // Guided by the voice, the pupil's own button hides (ADR-035, decision 7):
    // "↻ Nog eens" hears a step again. Their "aan" is kept for text.
    if (voice) ui.setVoice(!voiceShown() || guide === 'voice' ? 'hidden' : voice.on ? 'on' : 'off');
  };
  /** The class's Voorlezen as last seen, to tell it coming back on. */
  let voiceSwitchOn = engine?.input.voice ?? true;
  /** The teacher's pause as last seen, to tell the class resuming. */
  let classPaused = engine?.input.paused ?? false;
  // The teacher's switch and pause, now and whenever they change (a late
  // joiner has them already): the player stops itself, the button follows,
  // and the guide falls back to text or comes back (a pause holds its timer).
  const stopFollowingVoice =
    voice && engine
      ? engine.onInput((input) => {
          const back = input.voice && !voiceSwitchOn;
          const resumed = !input.paused && classPaused;
          voiceSwitchOn = input.voice;
          classPaused = input.paused;
          pacer.paused(input.paused);
          if (back) {
            pacer.restarted();
            autoplay?.restarted();
          }
          applyGuide();
          // The voice asked to start again (it has already followed the
          // switch): Voorlezen back on, or the class resuming mid-voice.
          if ((back || resumed) && voice.due && shownEntry === entryCount) armStall(entryCount);
        })
      : (): void => {};
  ui.onVoice(() => voice?.toggle());

  /** How the step is guided, from the guide, the class's Voorlezen, and what the step's voice has. */
  const renderFor = (): GuideRender => {
    const step = player.state.step;
    return guideFor({
      guide,
      voiceSwitch:
        engine === undefined
          ? undefined
          : classVoice({ inClass, stateSeen: classStateSeen, voice: engine.input.voice }),
      mode: lesson.mode,
      hasVoice: voice?.available === true,
      hasCues: step.voiceover !== undefined && cues[step.voiceover] !== undefined,
      stalled,
      // A furnish step's text stays: the pupil works under it (ADR-037).
      keepText: step.task?.type === 'furnish',
    });
  };
  /** The guide as it stands, to the lectern, the pacer, the button and the step counter. */
  function applyGuide(): void {
    const next = renderFor();
    if (next !== render) guideLog(`${player.state.step.id}: ${render} -> ${next}`);
    render = next;
    ui.setGuide(render);
    pacer.setActive(render !== 'text');
    if (autoplay?.active) autoplay.update(autoplayFacts());
    showVoice();
    showHud();
  }
  function voiceChanged(): void {
    showVoice();
    // A file this browser cannot decode: the step falls back to text.
    if (render !== 'text' && voice?.available !== true) applyGuide();
  }
  /** A step's voice heard to its end, or cut by an answer (ADR-035): said to the class, once per entry. */
  function heard(stepId: string): void {
    guideLog(`${stepId}: voice done`);
    // Racing a goto, it names the step just left; the hub drops that quietly.
    if (inClass) session?.voiceDone(stepId);
  }
  /** The voice has ended, every time (a replay's end, a restart's): what the pacer waits for. */
  function voiceEnded(stepId: string): void {
    if (player.state.step.id !== stepId) return;
    pacer.voiceEnded();
    autoplay?.voiceEnded();
    // A prediction whose question no sentence opens: the question at the voice's end.
    if (player.state.phase === 'intro' && render !== 'text') player.beginPredict();
  }
  /** Outside a class: the start card's toggle and G. */
  const setGuide = (next: Guide): void => {
    if (!interactive || next === guide) return;
    guide = next;
    guideLog(`guide: ${next}`);
    stalled = false;
    voice?.setMode(next === 'voice' ? 'guide' : 'button');
    pacer.restarted();
    autoplay?.restarted();
    applyGuide();
    if (shownEntry === entryCount) armStall(entryCount);
  };
  /**
   * Decision 11: a voice not begun 3 s after it was due leaves the text box
   * for the rest of the step (engine/guide-stall.ts). Armed when the panel
   * shows, and whenever the voice is asked to start over -- before asking,
   * as a decoded voice may begin at once.
   */
  let stallEntry = 0;
  const stall = new StallWatch({
    schedule: later,
    starts: () => voice?.starts ?? 0,
    applies: () =>
      stallEntry === entryCount &&
      // Kept back until 2 s in the world: not late, and watched again from the entry.
      voice?.held !== true &&
      render !== 'text' &&
      engine !== undefined &&
      engine.input.voice &&
      !engine.input.paused,
    onStall: () => {
      stalled = true;
      guideLog(`${player.state.step.id}: voice late, text box`);
      applyGuide();
    },
  });
  const cancelStall = (): void => stall.cancel();
  const armStall = (entry: number, seconds?: number): void => {
    stall.cancel();
    if (render === 'text' || !voice) return;
    stallEntry = entry;
    stall.arm(seconds);
  };
  ui.onForward(() => {
    if (pacer.pressForward()) player.next();
  });
  ui.onHotspot((open) => {
    pacer.hotspot(open);
    autoplay?.hotspot(open);
  });
  // A teleport's fade to black ends what the voice was saying.
  stage.onTravel(() => voice?.stop());

  // The library's models (ADR-043; render/models/): their GLBs from the signed URLs, a mixer
  // each, their walks and plays, their sounds on the models bus, and Spannend -- read at each
  // step's entry, warned of on the lectern for 3 s, "Sla over" local to this pupil. VR only.
  const catalogue: ModelCatalogue = options.models ?? {};
  const modelIds = isAr
    ? []
    : Object.entries(lesson.objects ?? {})
        .filter(([, object]) => modelSlugOf(object.kind) !== undefined)
        .map(([id]) => id);
  const spannend = new SpannendSwitch(inClass ? classSpannend : localSpannend);
  /** The first step's models wait for the class's first session.state (setSpannend). */
  let firstWaiting = false;
  /** The teacher's pause, as the teacher's window onto the class hears it (no engine there). */
  let windowPaused = false;
  const skipSpannend = (): void => {
    if (models?.skip()) guideLog(`${player.state.step.id}: Sla over`);
  };
  /**
   * The lectern as a Spannend part goes: the warning card for its 3 s (standing through every
   * redraw), "Sla over" while it can be pressed -- warning, part and after it in the step.
   */
  const spannendChanged = (event: SpannendEvent): void => {
    if (!running || !interactive) return;
    if (event.phase === 'warning') {
      ui.showCard(SPANNEND_TEXT.nl.title, SPANNEND_TEXT.nl.comes(event.labels), [], {
        sticky: true,
      });
    } else {
      ui.clearCard();
    }
    const skippable = event.phase === 'warning' || event.phase === 'part' || event.phase === 'done';
    ui.setSkip(skippable ? skipSpannend : undefined);
    if (!skippable) ui.setVeiled(false);
  };
  const models =
    modelIds.length > 0
      ? new ModelRuntime({
          lesson,
          catalogue,
          worlds,
          objects: new Map(
            modelIds.flatMap((id) => {
              const object = built.objects.get(id);
              return object ? [[id, { group: object.group, body: object.body }] as const] : [];
            }),
          ),
          load: (facts) => {
            const url = media[facts.media];
            return url
              ? fetchBytes(url).then((bytes) => parseModel(bytes))
              : Promise.reject(new Error(`no signed URL for ${facts.media}`));
          },
          // Not in a teacher's window onto the class: no engine there.
          sound:
            interactive && engine
              ? new ModelSoundBank({
                  output: engine.modelsOutput(),
                  urlOf: (id) => media[id],
                  fetchBytes,
                })
              : undefined,
          eye: (into) => stage.activeCamera.getWorldPosition(into),
          // On the world's land as they walk (flat at 0 on the floor).
          ground: source?.definition.ground,
          // A 360° photo's floor takes no sun shadow: contact shadows instead.
          unlitFloor: source?.definition.unlitFloor,
          // In a dark world, their own soft light (ADR-100); in daylight it adds nothing.
          light: look.modelLight,
          onSpannend: spannendChanged,
          // In a class, a ring's lap keeps the class's time; solo, a test drive, a preview: its own.
          classTime: inClass ? () => classClock.time(performance.now(), lesson.steps) : undefined,
        })
      : undefined;
  // The desktop harness reads where the models are and what Spannend is doing; never built in.
  if (import.meta.env.DEV && models) {
    Object.assign(window, {
      __models: models,
      __modelsHeld: () => ({
        paused: engine?.input.paused === true,
        travelling: stage.travelling,
        loaded: sceneLoaded,
      }),
    });
  }
  /** How long a step's changes take, its models' and a Spannend part's warning included. */
  const changeSeconds = (index: number): number => {
    const step = lesson.steps[index];
    if (!step) return 0;
    if (!models) return stepSeconds(step);
    const part = spannend.entered && step.spannend !== undefined;
    const seconds = Math.max(
      stepSeconds(step),
      modelSeconds(lesson, index, catalogue, { spannend: part, worlds }),
    );
    return seconds + (part ? SPANNEND.warningSeconds : 0);
  };
  /** Whether a step changes anything that plays: its objects, or its models. */
  const changes = (step: Step): boolean =>
    hasChange(step) || (models !== undefined && modelsChange(step));
  // Who made what the lesson shows and plays (ADR-043): "Bronnen" on the last step.
  const credits = lessonCredits(
    lesson,
    catalogue,
    plan ? recordingsOf(plan, source?.manifest) : [],
    source?.credits,
  );
  if (interactive && credits.length > 0) {
    ui.onCredits(() =>
      ui.showCard(CREDITS_TEXT.title, creditsText(credits), [
        { label: CREDITS_TEXT.close, onSelect: () => ui.render(player.state), kind: 'secondary' },
      ]),
    );
  }

  // Change within a lesson (render/motion.ts): forward by one step plays the
  // step's change, anything else snaps to the scene at that step. VR only;
  // time stands still during a teleport, so the change is seen, not missed.
  const motion = isAr
    ? undefined
    : new SceneMotion(
        lesson,
        new Map(
          [...built.objects].map(([id, object]) => [
            id,
            {
              group: object.group,
              body: object.body,
              label: object.label,
              orbiting: object.parent !== undefined && object.orbit !== undefined,
              // A library model walks and turns itself (render/models/runtime.ts).
              walks: modelIds.includes(id),
            },
          ]),
        ),
        { hold: () => stage.travelling },
      );
  /** The step whose scene is shown, or is being played towards. */
  let sceneIndex: number | undefined;
  /**
   * The scene for a new step: played if the class moved on by one, otherwise
   * snapped -- at the black of the fade when there is one, so it happens
   * unseen. Whatever was still playing is finished first.
   */
  const changeScene = (index: number): (() => void) | undefined => {
    if (!motion) return undefined;
    const previous = sceneIndex;
    sceneIndex = index;
    if (isForwardByOne(previous, index)) {
      motion.forward(index);
      models?.enterStep(index, { forward: true, spannend: spannend.enter() });
      // The world's features too (ADR-032): a volcano works up to its eruption.
      setWorldStates(worldStateAt(lesson, index, worlds), true);
      return undefined;
    }
    motion.finish();
    // The lesson's first step plays its models from where the lesson declares them -- in a
    // class once its first session.state is in, so a Spannend switched on before it applies.
    const first = previous === undefined && index === 0;
    firstWaiting = first && inClass && !classStateSeen;
    if (first && !firstWaiting) models?.enterStep(0, { forward: true, spannend: spannend.enter() });
    return () => {
      // A later step may have taken over while the fade ran.
      if (sceneIndex !== index) return;
      motion.snapTo(index);
      if (!first) models?.enterStep(index, { forward: false, spannend: spannend.enter() });
      setWorldStates(worldStateAt(lesson, index, worlds), false);
    };
  };
  const replay = (): void => {
    const index = player.state.index;
    const step = player.state.step;
    const change = motion !== undefined && replayOffered(lesson, index, worlds);
    // Guided by the voice, "Nog eens" is the voice again too (ADR-035).
    const guided = render !== 'text';
    if (!change && !guided) return;
    if (change) {
      sceneIndex = index;
      motion.replay(index);
      models?.replay(index, spannend.entered);
      setWorldStates(worldStateAt(lesson, index - 1, worlds), false);
      setWorldStates(worldStateAt(lesson, index, worlds), true);
    }
    // Watched from before it is asked: a decoded voice begins at once.
    if (guided && shownEntry === entryCount) armStall(entryCount);
    // In button mode it leaves the voice alone: two separate choices of the pupil's.
    voice?.replayed();
    if (guided) {
      guideLog(`${step.id}: again`);
      pacer.replayed(change && changes(step) ? MOTION_TIMING.still + changeSeconds(index) : 0);
      autoplay?.restarted();
      if (change && changes(step)) autoplay?.changeEnds(MOTION_TIMING.still + changeSeconds(index));
    }
  };
  if (interactive) ui.onReplay(replay);

  // Where the pupil stands and the VR lectern goes for each step: frame first,
  // then slide aside (engine/step-placement.ts). Placed when a step begins,
  // never for a hotspot or an answer. Not in AR: no views there, and the AR
  // panel is the pupil's to move.
  const frameAids = !isAr && interactive && desktop.frame ? new FrameAids(stage) : undefined;
  /** The sentence the plate shows, for the step counter, and how many the step has. */
  let hudCue: number | undefined;
  let hudCount = 0;
  const showHud = (): void => {
    const state = player.state;
    const lectern = placement?.lectern;
    const aside =
      !lectern || lectern.side === 0
        ? ''
        : ` · paneel opzij (${lectern.degrees}° ${lectern.side > 0 ? 'rechts' : 'links'})`;
    // Pulled closer, because models left it no room on its arc.
    const nearer =
      lectern?.distance === undefined
        ? ''
        : ` · paneel dichterbij (${lectern.distance.toFixed(2).replace('.', ',')} m)`;
    const changes = isAr ? '' : changeNote(state.step);
    // "stem · zin 2/5" while the voice guides (ADR-035).
    const guided =
      render === 'text'
        ? ''
        : ` · stem${hudCue === undefined ? '' : ` · zin ${hudCue + 1}/${hudCount}`}`;
    hud.textContent = `${state.index + 1}/${lesson.steps.length} · ${state.step.title}${aside}${nearer}${changes}${guided}`;
  };
  const placement = isAr
    ? undefined
    : new StepPlacement({
        stage,
        ui,
        geometry: built.geometry,
        steps: lesson.steps,
        currentStep: () => player.state.step,
        // The furnish step's side lectern, 45° to the left (ADR-037).
        fixed: (step) => (step.task?.type === 'furnish' ? FURNISH_PLACEMENT : undefined),
        onPlaced: (lectern, subject, eye, assembly) => {
          frameAids?.show(subject, eye, assembly);
          // A world feature's ⓘ (ADR-087) goes with the lectern: placed for where the pupil
          // stands and the lectern stands, again when the eyes' height arrives.
          const step = player.state.step;
          const rig = stage.rig;
          badges.showFeatures(
            featureBadgesFor({
              step,
              features: (built.geometry.framingFor?.(step) ?? built.geometry.framing).features,
              rig: {
                eye: [rig.position.x, rig.position.y + eye, rig.position.z],
                yaw: rig.rotation.y,
                eyeHeight: eye,
              },
              lectern,
              assembly,
            }),
          );
          showHud();
        },
      });

  // The villa's furniture (ADR-037): the pieces, the side lectern's rooms and palette, and what
  // presses do to them. Not in a teacher's window onto the class, and never in AR.
  const space = placementSpaceOf(source);
  const furnishes = !isAr && interactive && isFurnishLesson(lesson);
  if (furnishes && !space) {
    // A package that lost its plaatsruimte: the pupils can still look round, but nothing is placed.
    putNote('Plaatsruimte', 'Plaatsruimte: deze wereld heeft er geen');
  }
  const furnishing =
    furnishes && space
      ? furnishWorld({
          stage,
          ui,
          player,
          media,
          space,
          furniture: options.furniture,
          clock,
          putNote: (text) => putNote('Meubels:', text),
        })
      : undefined;

  // A headset knows the eyes' height only after its first steady frames:
  // until then everything was placed for 1.6 m. Placed again, without a fade.
  stage.onEyeKnown(() => {
    placement?.eyeKnown();
    badges.placeDirectionsAgain();
  });

  // One bench per step that asks for something to be put together (ADR-025).
  // Not in a teacher's preview: a window onto the class has no hands in it.
  const benches = new Map<string, AssemblyBench>();
  if (interactive) {
    for (const step of lesson.steps) {
      if (step.task?.type !== 'assemble') continue;
      const bench = new AssemblyBench(built.root, built.objects, {
        parts: step.task.parts,
        snapMetres: step.task.snapMetres,
        // The lesson and the step, so every headset in the room lays out the
        // same pile and a teacher can point at "the one on the far left".
        seed: `${lesson.id}/${step.id}`,
      });
      bench.onPlaced((placed, total) => ui.showAssemblyProgress(player.state, placed, total));
      bench.onDone((result) => {
        player.completeAssembly(result);
        const hardest = result.hardest ? lesson.objects?.[result.hardest]?.label : undefined;
        const read = outcomeSeconds(ui.showAssemblyOutcome(hardest, player.state));
        pacer.resultShown(read);
        autoplay?.resultShown(read);
      });
      benches.set(step.id, bench);
    }
  }

  // Padlocks, locked to begin with (Placeable). A student reaching for a bone
  // kept picking up the whole skeleton; now nothing moves until they ask it
  // to. Unlocked, handles appear beside it: move and resize, used by pointing
  // from wherever the student stands -- carrying by hand meant walking up to
  // a skeleton 1.6 m away first (handles.ts). Only where there is something
  // to place.
  const dragger = new Dragger();
  const locks: LockButton[] = [];
  const toolbars: Toolbar[] = [];
  const handleTargets = new Map<HandleButton, Draggable>();
  // The trays live in the skeleton's space but have handles of their own, so
  // moving the skeleton leaves them where the student put them.
  const skeleton: Draggable = {
    object: built.root,
    resizable: true,
    stays: [...benches.values()].map((bench) => bench.tray),
  };

  // What the skeleton stands on while it is unlocked: the table top or the
  // floor beneath it, found by a hit-test from the eye at its feet (surfaces.ts).
  // Moving kept whatever height it had, so a skeleton shrunk to fit a desk
  // stood on the floor under it.
  const room = isAr ? new DemoRoom() : undefined;
  const surfaces = placeable && room ? new Surfaces(stage.renderer.xr, room.surfaces) : undefined;

  const hasObjects = Object.keys(lesson.objects ?? {}).length > 0;
  if (placeable && interactive && hasObjects) {
    // Beside the skeleton's head, where it was placed on a Quest 3; beside
    // the top-left of anything smaller (engine/lock-anchor.ts).
    const anchor = new THREE.Vector3(...lockAt);
    const lock = new LockButton(built.root, anchor);
    lock.onToggle((locked) => {
      placeable.locked = locked;
      // Out of the way while the skeleton is being moved: in front of it, the
      // panel hid the handles of a skeleton shrunk to fit a desk.
      ui.stepAside(!locked);
      if (locked) dragger.end();
    });
    const move = new HandleButton(built.root, 'move');
    const resize = new HandleButton(built.root, 'resize');
    handleTargets.set(move, skeleton);
    handleTargets.set(resize, skeleton);
    locks.push(lock);
    toolbars.push(new Toolbar(built.root, anchor, lock, [move, resize]));
  }

  // Each tray: its padlock, and its rail as the handle -- taken hold of like
  // a trolley's, all the way round.
  const rails: { bench: AssemblyBench; lock: LockButton; target: Draggable }[] = [];
  for (const bench of benches.values()) {
    // A child of the tray, so it comes and goes with it and moves with it.
    const lock = new LockButton(bench.tray, bench.lockSpot);
    lock.onToggle((locked) => {
      bench.setMovable(!locked);
      if (locked) dragger.end();
    });
    locks.push(lock);
    rails.push({ bench, lock, target: { object: bench.tray, resizable: false } });
  }
  const railsLive = rails.map((rail) => ({
    path: () => rail.bench.railPath(),
    shown: () => shown(rail.bench.tray) && !rail.lock.locked,
  }));
  const handleButtons = (): HandleButton[] =>
    [...handleTargets.keys()].filter((handle) => shown(handle.mesh));

  // Handles first, then bones, then two-handed resizing: a hand on a handle is
  // not reaching for a bone, and a hand holding a bone is not resizing.
  const stopPlacing = stage.onFrame((dt, _elapsed, frame) => {
    const busy = new Set<number>();
    const eye = stage.activeCamera.getWorldPosition(new THREE.Vector3());

    for (const [index, hand] of stage.hands.states.entries()) {
      if (dragger.hand === index) {
        if (!hand.tracked || !hand.pinching) dragger.end();
        else {
          dragger.moveHand(index, hand.pinchPoint);
          busy.add(index);
        }
        continue;
      }
      if (dragger.active || !hand.tracked || !hand.justPinched) continue;

      const near = handleNear(hand.pinchPoint, handleButtons(), railsLive);
      if (near?.kind === 'button') {
        dragger.beginHand(
          near.button.kind,
          handleTargets.get(near.button)!,
          index,
          hand.pinchPoint,
          eye,
        );
        busy.add(index);
      } else if (near?.kind === 'rail') {
        dragger.beginHand('move', rails[near.index]!.target, index, hand.pinchPoint, eye);
        busy.add(index);
      }
    }

    for (const bench of benches.values()) {
      bench.update(dt, stage.hands, busy).forEach((hand) => busy.add(hand));
    }
    if (!dragger.active) placeable?.update(stage.hands, busy);

    if (surfaces && placeable && !placeable.locked) {
      surfaces.update(frame);
      const feet = built.root.getWorldPosition(new THREE.Vector3());
      const height = surfaces.below(feet.x, feet.z);
      if (height !== undefined && Math.abs(height - feet.y) > 0.002) settle(skeleton, height);
    }

    for (const toolbar of toolbars) toolbar.update(stage.activeCamera);
    for (const lock of locks) lock.update(stage.activeCamera);
  });

  /** A padlock the ray lands on, if any. Hidden ones are not there to press. */
  const lockAlong = (ray: THREE.Ray): LockButton | undefined => {
    pointer.set(ray.origin, ray.direction);
    const shownLocks = locks.filter((lock) => shown(lock.mesh)).map((lock) => lock.mesh);
    const hit = pointer.intersectObjects(shownLocks, false)[0];
    return hit?.object.userData['lock'] as LockButton | undefined;
  };

  /** A handle or live rail the ray lands on, and where. */
  const handleAlong = (
    ray: THREE.Ray,
  ): { kind: HandleKind; target: Draggable; at: THREE.Vector3 } | undefined => {
    pointer.set(ray.origin, ray.direction);
    const hit = pointer.intersectObjects(
      handleButtons().map((handle) => handle.mesh),
      false,
    )[0];
    const handle = hit?.object.userData['handle'] as HandleButton | undefined;
    if (hit && handle)
      return { kind: handle.kind, target: handleTargets.get(handle)!, at: hit.point };

    // A rail by coming close to it, not by hitting its thin tube (railAlong).
    let nearest: { target: Draggable; at: THREE.Vector3 } | undefined;
    for (const rail of rails) {
      if (!shown(rail.bench.tray) || rail.lock.locked) continue;
      const at = railAlong(ray, rail.bench.railPath());
      if (at && (!nearest || at.distanceTo(ray.origin) < nearest.at.distanceTo(ray.origin))) {
        nearest = { target: rail.target, at };
      }
    }
    return nearest ? { kind: 'move', ...nearest } : undefined;
  };

  // In a class the last step asks the teacher to be let out. Practising alone
  // there is nobody to ask and the lobby is one press of the headset's own
  // button away. A played preview brings its own way out, which ends it.
  if (options.onFinish) {
    ui.onFinish(options.onFinish);
  } else if (interactive && currentSessionId !== undefined) {
    ui.onFinish(() => session?.finished());
  }
  const badges = new HotspotBadges(stage.scene);
  const measure = room ? new MeasureTool(stage, room) : undefined;
  /** A step's direction hotspots (ADR-036): each one that the platform has pointed, in the room. */
  const directionsOf = (index: number): DirectionEntry[] => {
    const yaw = look.pano?.yawAt(index) ?? 0;
    return (lesson.steps[index]?.hotspots ?? []).flatMap((hotspot, at) =>
      hotspot.toward === undefined
        ? []
        : [
            {
              key: directionKey(at),
              title: hotspot.title,
              direction: towardDirection(hotspot.toward, yaw),
            },
          ],
    );
  };
  const eyeNow = new THREE.Vector3();
  /** What is drawn over a feature's ⓘ and stops a ray before it: the panel and the plate, as shown. */
  const lecternSurfaces = (): THREE.Object3D[] =>
    pickable([ui.panelMesh, ...(ui.subtitleMesh ? [ui.subtitleMesh] : [])]);

  let lastStepId: string | undefined;

  let lastPhase: StepPhase | undefined;
  const applyStep = (state = player.state): void => {
    const stepChanged = state.step.id !== lastStepId;
    const isLast = state.index === lesson.steps.length - 1;

    // What the last step was saying stops; this step's file is made ready,
    // and with the pupil's Voorlezen on -- or the class guided by the voice --
    // it reads itself once its panel shows.
    let readWhenShown: (() => void) | undefined;
    const entry = stepChanged ? ++entryCount : entryCount;
    if (stepChanged) {
      readWhenShown = voice?.stepChanged(state.step.voiceover, state.step.id);
      stalled = false;
      cancelStall();
      hudCue = undefined;
      pacer.enter({
        control: player.control,
        phase: pacedPhase(state.phase, state.step.task),
        isLast,
      });
      autoplay?.enter({ ...autoplayFacts(state), isLast });
    } else if (lastPhase !== state.phase && (lastPhase === 'quiz' || lastPhase === 'predict')) {
      // An answer given while the voice reads stops it, and counts as heard (ADR-035).
      voice?.answered();
      pacer.voiceEnded();
      autoplay?.voiceEnded();
    }
    const answeredPrediction = !stepChanged && lastPhase === 'predict' && state.phase === 'result';
    lastPhase = state.phase;
    pacer.update({
      control: player.control,
      phase: pacedPhase(state.phase, state.step.task),
      isLast,
    });
    // "Jij koos: … Zo meteen zie je het.", read for 4 s.
    if (answeredPrediction) {
      pacer.resultShown(PREDICTED_SECONDS);
      autoplay?.resultShown(PREDICTED_SECONDS);
    }
    // Before drawing: the panel's body shows or gives way to the subtitle plate.
    applyGuide();
    ui.render(state);

    // Measurements belong to the step that asked for them; they survive the
    // move from task to result so the student can see what the number refers
    // to, and are cleared on the way to the next step.
    if (furnishing) {
      furnishing.lectern.shown = state.step.task?.type === 'furnish';
      // Leaving the furnish step lets go of what is held.
      if (stepChanged) furnishing.interaction.stepChanged();
    }
    if (stepChanged) {
      measure?.reset();
      // A new task: pin the panel where the student is standing now. They have
      // just tapped a button on it, so they are certainly facing it.
      if (isAr) ui.pinArPanel();
      lastStepId = state.step.id;
    }

    // Focus dims everything else. The Moon is a declared child of Earth, so
    // "the focused object and its children" replaces the prototype's
    // special case for it.
    const focus = state.step.focus;
    // Sixteen bones in a column is sixteen name-tags on top of each other,
    // which is a wall of text rather than labelling. A lesson with many
    // objects close together asks for "on-demand" and gets a name only where
    // this step is pointing: its focus, or the objects its hotspots name.
    const onDemand = lesson.labels === 'on-demand';
    const named = new Set((state.step.hotspots ?? []).map((hotspot) => hotspot.object));

    for (const [id, object] of built.objects) {
      const isChild = object.parent !== undefined && object.parent === focus;
      // The highlighted object's name shows: "this one".
      const pointedAt = id === focus || isChild || named.has(id) || id === state.step.highlight;
      object.label.visible = onDemand ? pointedAt : !focus || pointedAt;
    }

    // Only when the step changes: an answer or a hotspot never moves the
    // pupil or the lectern. A view is placed at the black of the fade, for the
    // eyes' height then; without one, the lectern slides from where the pupil
    // stands, if the step is about something else.
    if (stepChanged) {
      // Whether this step's change plays (forward by one), for the pacer.
      const plays =
        motion !== undefined && isForwardByOne(sceneIndex, state.index) && changes(state.step);
      const scene = changeScene(state.index);
      // At the black of the fade: the scene for the step, and any world file
      // that arrived late (ADR-032).
      // A 360° photo (ADR-036): a different photo always goes in through black,
      // view or not; at the black the photo of the step the pupil is on then,
      // held up to 3 s when it is late, and that step's direction badges.
      const pano = look.pano;
      const travel = pano?.differs(state.index) === true;
      const atArrival = (): void => {
        scene?.();
        look.atStep();
        if (pano) {
          const index = player.state.index;
          pano.arrive(index, {
            hold: (hint) => stage.holdBlack(hint),
            release: () => stage.fadeIn(TELEPORT_SECONDS),
          });
          badges.showDirections(directionsOf(index));
        }
      };
      // The last step's feature badges go now; this step's come with its lectern (onPlaced).
      badges.clearFeatures();
      if (placement) placement.stepChanged(state.step, atArrival, { travel });
      else atArrival();
      // After the teleport's fade (or the lesson's opening one), not in the dark.
      const stepIndex = state.index;
      stage.whenClear(() => {
        // The next step's photo, while this one is seen (at most two held).
        pano?.preload(player.state.index + 1);
        readWhenShown?.();
        if (entry !== entryCount) return;
        shownEntry = entry;
        if (plays) {
          pacer.changeEnds(MOTION_TIMING.still + changeSeconds(stepIndex));
          autoplay?.changeEnds(MOTION_TIMING.still + changeSeconds(stepIndex));
        }
        armStall(entry);
      });
    }

    badges.show(
      (state.step.hotspots ?? [])
        .map((hotspot) =>
          hotspot.object === undefined ? undefined : built.objects.get(hotspot.object),
        )
        .filter((object): object is NonNullable<typeof object> => object !== undefined),
    );

    // Only a *measure* task wants the measuring tool. `assemble` is a task
    // too (ADR-025), and a measuring reticle in front of a child on a step
    // about bones is an interaction that looks deliberate and teaches the
    // wrong thing.
    const measuring = state.step.task?.type === 'measure';
    measure?.setActive(state.phase === 'task' && measuring);

    // The pile is out only while its step is being built. Every other step --
    // including this one once it is finished -- shows the skeleton whole.
    for (const [stepId, bench] of benches) {
      if (state.step.id === stepId && state.phase === 'task') {
        bench.enter();
        // Come back to a half-built skeleton and the count is still right.
        if (bench.placed > 0) ui.showAssemblyProgress(state, bench.placed, bench.total);
      } else {
        bench.leave();
      }
    }
    showHud();
  };

  player.onChange((state) => {
    applyStep(state);
    // Only what the headset did on its own. Echoing back the step the teacher
    // just sent would be a loop, and in a locked class the server would
    // refuse it anyway.
    if (state.step.id !== remoteStep) session?.progress(state.step.id);
  });

  // Interactions that need more than the player's own state transition.
  const originalAnswerQuiz = player.answerQuiz.bind(player);
  const answerQuiz = (choice: number): void => {
    const quiz = player.state.step.quiz;
    const outcome = originalAnswerQuiz(choice);
    if (!outcome || !quiz) return;
    const said = ui.showQuizOutcome(
      outcome.correct,
      quiz.options[outcome.correctIndex] ?? '',
      quiz.explain,
      player.state,
    );
    // Read, then on by itself where the voice guides (ADR-035, decision 3).
    pacer.resultShown(outcomeSeconds(said));
    autoplay?.resultShown(outcomeSeconds(said));
  };

  /** What the demo's autoplay is told of the step as it stands (engine/autoplay.ts). */
  function autoplayFacts(state = player.state): {
    phase: StepPhase;
    isLast: boolean;
    guided: boolean;
    voiceDue: boolean;
    readSeconds: number;
  } {
    const step = state.step;
    const words =
      state.phase === 'quiz' && step.quiz
        ? [step.quiz.question, ...step.quiz.options]
        : state.phase === 'predict' && step.predict
          ? [step.predict.question, ...step.predict.options]
          : // No panel: the narration shown in its place where nobody speaks it (engine/step-text.ts).
            [
              step.panel?.title ?? step.title,
              step.panel?.text ?? unspokenNarration(step, render) ?? '',
            ];
    return {
      phase: pacedPhase(state.phase, step.task),
      isLast: state.index === lesson.steps.length - 1,
      guided: render !== 'text',
      voiceDue:
        voice?.available === true &&
        step.voiceover !== undefined &&
        (render !== 'text' || voice.on),
      readSeconds: readingSeconds(words.join(' ')),
    };
  }

  /** The demo's autoplay doing what a pupil would: the question opened, answered, on, done. */
  function autoplayAct(move: AutoplayMove): void {
    const state = player.state;
    guideLog(`${state.step.id}: autoplay ${move}`);
    switch (move) {
      case 'begin':
        player.beginPredict();
        return;
      case 'answer': {
        const choice = autoplayChoice(state.step);
        if (choice === undefined) return;
        if (state.phase === 'quiz') answerQuiz(choice);
        else if (state.phase === 'predict') player.answerPredict(choice);
        return;
      }
      case 'next':
        // As a press of Verder: the pacer's own timer goes with it.
        pacer.pressForward();
        player.next();
        return;
      case 'finish':
        options.onFinish?.();
        return;
    }
  }

  measure?.onMeasured((metres) => {
    const step = player.state.step;
    const outcome = player.completeTask(metres);
    const chosen = step.predict?.options[outcome.estimate] ?? '';
    const actual = step.predict?.options[outcome.actualIndex] ?? '';
    const said = ui.showMeasureOutcome(
      `Gemeten: ${formatMetres(outcome.measuredMeters)}. Jij schatte: ${chosen.toLowerCase()}.` +
        (outcome.correct ? ' Je oog is goed getraind.' : ` Het was ${actual.toLowerCase()}.`),
      outcome.correct,
      player.state,
      () => {
        player.retryTask();
        measure.reset();
      },
    );
    pacer.resultShown(outcomeSeconds(said));
  });

  // Grabbing a piece of furniture (ADR-037): in VR, in the furnish step, by controller, hand or mouse.
  if (furnishing) {
    const { interaction } = furnishing;
    stage.onGrab(
      (ray) => interaction.claim(ray),
      (ray) => interaction.move(ray),
      () => interaction.end(),
    );
  }

  // Grabbing the panel takes priority over everything else a press can do.
  if (isAr && interactive) {
    let holding: 'panel' | 'part' | 'handle' | undefined;
    stage.onGrab(
      (ray, controller) => {
        if (lockAlong(ray)) return false; // handled as a select, below
        // A handle, by any pointer -- a hand's ray too: pointing at the
        // skeleton's handle is how a student moves it without walking to it.
        // But a hand whose fingertips are already on a handle is gripping it,
        // and the frame loop owns that grip; taking it here as well would
        // start two drags pulling the same thing two ways.
        const source = controller?.userData['source'] as XRInputSource | undefined;
        if (source?.hand) {
          const pinch =
            stage.hands.states[
              stage.controllers.findIndex((candidate) => candidate === controller)
            ];
          if (pinch?.tracked && handleNear(pinch.pinchPoint, handleButtons(), railsLive)) {
            return false;
          }
        }
        const handle = handleAlong(ray);
        if (handle) {
          dragger.beginPointer(handle.kind, handle.target, ray, handle.at);
          holding = 'handle';
          return true;
        }
        if (ui.tryGrab(ray)) {
          holding = 'panel';
          return true;
        }
        // A tracked hand's pinch arrives here too, as a select along the
        // hand's ray. Hands take hold where they pinch (AssemblyBench.update),
        // not along a ray, so a child cannot pull a bone from across the room.
        if (source?.hand) return false;

        for (const bench of benches.values()) {
          if (bench.grabWithRay(ray)) {
            holding = 'part';
            return true;
          }
        }
        return false;
      },
      (ray) => {
        if (holding === 'panel') ui.dragTo(ray);
        else if (holding === 'handle') dragger.movePointer(ray);
        else if (holding === 'part') for (const bench of benches.values()) bench.moveRay(ray);
      },
      () => {
        if (holding === 'panel') ui.endDrag();
        else if (holding === 'handle') dragger.end();
        else if (holding === 'part') for (const bench of benches.values()) bench.endRay();
        holding = undefined;
      },
    );
  }

  // A preview is a window onto the class, not a second pair of hands in it.
  if (interactive)
    stage.onSelect((ray, _source, inputSource) => {
      if (stage.travelling) return;

      const raycaster = new THREE.Raycaster();
      raycaster.set(ray.origin, ray.direction);

      // A padlock is a button too, and the first thing a student presses
      // before moving anything.
      const lock = lockAlong(ray);
      if (lock) {
        lock.toggle();
        return;
      }

      // The side lectern's rooms, palette and turning buttons (ADR-037).
      const furnishPress = furnishing?.lectern.along(ray);
      if (furnishPress) {
        furnishing!.interaction.press(furnishPress.action);
        return;
      }

      // UI first: a button in front of a planet must win.
      const button = raycaster.intersectObjects(ui.pickables, false)[0];
      if (button) {
        const target = button.object.userData['button'] as { onSelect: () => void } | undefined;
        // Quiz buttons route through the wrapper so the outcome panel is shown;
        // "Voorlezen" and "Sla over" are not answers.
        if (
          player.state.phase === 'quiz' &&
          !ui.isVoiceButton(button.object) &&
          !ui.isSkipButton(button.object)
        ) {
          const index = ui.pickables.indexOf(button.object);
          if (index >= 0) answerQuiz(index);
        } else {
          target?.onSelect();
        }
        return;
      }

      if (player.state.phase === 'task') {
        // Same reason: a select during an assemble step must not place a
        // measuring point. It does nothing until that interaction exists,
        // which is honest -- doing the wrong thing is worse than doing none.
        if (player.state.step.task?.type === 'measure') measure?.select(ray, inputSource);
        return;
      }

      // A direction in a 360° photo (ADR-036): its badge, 7 m out.
      const direction = badges.directionAlong(ray);
      if (direction) {
        const opened = player.openHotspot(direction.key);
        if (opened) ui.showHotspot(opened.hotspot.title, opened.hotspot.text, player.state);
        return;
      }

      // An object's or a world feature's points, paged: "De klep · 1 van 3", with its next button.
      const openPaged = (key: string, page = 0): void => {
        const opened = player.openHotspot(key, page);
        if (!opened) return;
        ui.showHotspot(opened.hotspot.title, opened.hotspot.text, player.state, {
          page: opened.page,
          pages: opened.pages,
          onNext: () => openPaged(key, opened.page + 1),
        });
      };

      // A world feature (ADR-087): its badge, drawn over the world, is what a ray opens; never
      // through the lectern's panel, which hides it.
      const feature = badges.featureAlong(ray, lecternSurfaces());
      if (feature) {
        openPaged(feature.key);
        return;
      }

      // Never a hidden object: three.js raycasts invisible things.
      const bodies = pickable([...built.objects.values()].map((object) => object.body));
      const hit = raycaster.intersectObjects(bodies, false)[0];
      if (!hit) return;
      const objectId = hit.object.userData['objectId'] as string | undefined;
      if (!objectId) return;
      openPaged(objectId);
    });

  // Animation: spin the bodies, move the Moon, keep hover honest.
  /** A step's cues as the plate pages them, worked out once per voice-over. */
  const paged = new Map<string, (PlateContent & { start: number; end: number; cue: number })[]>();
  const pagesOf = (id: string): (PlateContent & { start: number; end: number; cue: number })[] => {
    let list = paged.get(id);
    if (!list) {
      list = (cues[id] ?? []).flatMap((cue, index) =>
        pages(cue, (text) => ui.fitsSubtitle(text)).map((page) => ({
          ...page,
          dim: false,
          cue: index,
        })),
      );
      paged.set(id, list);
    }
    return list;
  };
  /** While the voice loads: the step's title, dimmed. */
  const titles = new Map<Step, PlateContent>();
  const loadingTitle = (step: Step): PlateContent => {
    let title = titles.get(step);
    if (!title) {
      title = { text: step.title, dim: true };
      titles.set(step, title);
    }
    return title;
  };
  /** When a prediction's question appears (decision 4), per voice-over; undefined: at the voice's end. */
  const questions = new Map<Step, number | undefined>();
  const questionAt = (step: Step): number | undefined => {
    if (!questions.has(step)) {
      const id = step.voiceover;
      const question = step.predict?.question;
      questions.set(
        step,
        id === undefined || question === undefined
          ? undefined
          : questionCue(cues[id] ?? [], question),
      );
    }
    return questions.get(step);
  };
  /** Once a frame while the voice guides: the prediction's question, and the subtitle plate. */
  const followVoice = (speaking: VoicePlayer): void => {
    const state = player.state;
    const step = state.step;
    if (state.phase === 'intro' && speaking.state === 'playing') {
      const at = questionAt(step);
      if (at !== undefined && speaking.position() >= at) {
        guideLog(`${step.id}: the question, with its words`);
        player.beginPredict();
        return;
      }
    }
    let content: PlateContent | undefined;
    let cue: number | undefined;
    const list = step.voiceover === undefined ? undefined : pagesOf(step.voiceover);
    if (ui.subtitlesShown && !engine?.input.paused) {
      if (speaking.state === 'loading') content = loadingTitle(step);
      else if (speaking.started && list) {
        const index = cueAt(list, speaking.position());
        if (index !== undefined) {
          content = list[index];
          cue = list[index]!.cue;
        }
      }
    }
    ui.setSubtitle(content);
    if (cue !== hudCue) {
      hudCue = cue;
      hudCount = step.voiceover === undefined ? 0 : (cues[step.voiceover]?.length ?? 0);
      if (cue !== undefined) guideLog(`${step.id}: zin ${cue + 1}/${hudCount}`);
      showHud();
    }
  };

  const stopFrames = stage.onFrame((dt, elapsed, frame) => {
    motion?.update(dt);
    // The models stand still while the teacher pauses, at a teleport's black, and until the
    // world is in (ADR-043); the lectern fades out while one is inside the clear space round the stage in Spannend.
    const modelsMoved =
      models?.update(
        dt,
        engine?.input.paused === true || windowPaused || stage.travelling || !sceneLoaded,
      ).moving ?? false;
    if (models) ui.setVeiled(models.veiled);
    if (voice && render !== 'text') followVoice(voice);
    // The shadow is drawn again only while something in the lesson moves.
    // Held at a fade, as the lesson's motion is: the eruption builds up as the view fades in.
    look.update(
      dt,
      elapsed,
      moving || motion?.running === true || modelsMoved,
      stage.travelling,
      stage.black,
    );
    // The ears where the head is: world-locked sound (six plain values, nothing allocated).
    if (soundscape || models) audio?.listen(stage.activeCamera, { models: models !== undefined });
    perf?.update(dt);
    if (furnishing) {
      // The holding controller's thumbstick turns and colours the piece; the pieces arrive one a frame.
      furnishing.interaction.frame(dt, stage.thumbstick(stage.grabController));
      furnishing.view.update(elapsed, stage.activeCamera);
      furnishing.notice.update(clock(), stage.activeCamera);
      furnishing.lectern.hover(stage.pointingRays);
      furnishing.interaction.hover(stage.pointingRays);
    }
    for (const object of built.objects.values()) {
      if (object.spin) object.body.rotation.y += object.spin * dt;
      for (const spinner of object.spinners) spinner.object.rotation.y += spinner.rate * dt;

      if (object.parent && object.orbit) {
        const parent = built.objects.get(object.parent);
        if (parent) {
          const angle = elapsed * 0.25;
          object.group.position.set(
            parent.group.position.x + Math.cos(angle) * object.orbit,
            parent.group.position.y + Math.sin(angle) * 0.15,
            parent.group.position.z + Math.sin(angle) * object.orbit,
          );
        }
      }
    }

    badges.update(
      elapsed,
      (object) => !shown(object.group) || motion?.appearing(object.id) === true,
      look.pano
        ? {
            eye: stage.activeCamera.getWorldPosition(eyeNow),
            shown: look.pano.showsPhotoAt(player.state.index),
          }
        : undefined,
    );
    // The XR camera ignores the rig inside a frame handler: the head comes from the active camera.
    badges.updateFeatures(elapsed, stage.activeCamera.getWorldPosition(eyeNow));

    // Where each ray lands, so the line stops there and the cursor sits on
    // it. The UI is checked first and wins, exactly as it does when the
    // student actually presses: a button in front of a bone is the thing
    // being pointed at.
    const rays = stage.pointingRays;
    const onUi = ui.updateHover(rays);
    stage.setPointerReach(
      rays.map((ray, index) => {
        const button = onUi[index];
        if (button !== undefined) return button;

        // Otherwise the lesson's own objects. Without this a ray pointed at a
        // bone showed nothing at all -- a line into space, with no way for a
        // student to tell they had found it.
        pointer.set(ray.origin, ray.direction);
        const onLock = pointer.intersectObjects(
          locks.filter((lock) => shown(lock.mesh)).map((lock) => lock.mesh),
          false,
        )[0];
        if (onLock) return onLock.distance;
        const onHandle = handleAlong(ray);
        if (onHandle) return onHandle.at.distanceTo(ray.origin);
        const onDirection = badges.directionAlong(ray);
        if (onDirection) return onDirection.distance;
        const onFeature = badges.featureAlong(ray, lecternSurfaces());
        if (onFeature) return onFeature.distance;
        const onPiece = furnishing?.view.along(ray, REACH_METRES);
        if (onPiece) return onPiece.distance;
        pointer.set(ray.origin, ray.direction);
        return pointer.intersectObjects(pickable(pickableBodies), false)[0]?.distance;
      }),
    );
    measure?.update(frame);
  });

  if (interactive) window.addEventListener('keydown', onKey);
  function onKey(event: KeyboardEvent): void {
    // A held piece: Q and E turn it, 1–8 colour it (ADR-037).
    if (furnishing?.interaction.key(event.key)) return;
    // Verder, as on the lectern: from a predict step's introduction that is
    // its question, not the next step.
    // The arrow keys cancel the guide's timer (ADR-035).
    if (event.key === 'ArrowRight') {
      if (player.state.phase === 'intro') player.beginPredict();
      else if (pacer.pressForward()) player.next();
    }
    if (event.key === 'ArrowLeft') {
      pacer.pressForward();
      player.previous();
    }
    // "↻ Nog eens" on a laptop (the voice too, when it guides).
    if (event.key === 'r' || event.key === 'R') replay();
    // "Voorlezen", where the button shows; nothing while the voice guides.
    if (isVoiceKey(event) && voiceShown() && guide !== 'voice') voice?.toggle();
    // The guide, outside a class: text or voice.
    if (isGuideKey(event) && !inClass) {
      localGuide = guide === 'voice' ? 'text' : 'voice';
      setGuide(localGuide);
    }
  }

  // Nothing to offer someone already inside a session of the right kind, and
  // nothing to offer a teacher watching on a laptop.
  const removeXrButton =
    !interactive || stage.presenting
      ? (): void => {}
      : addXrButton(stage, lesson, {
          askName: recorded,
          reportTrouble: recorded,
          // A played lesson and the demo: the viewer's own sound toggle, under "Start in VR".
          soundToggle: !recorded && soundscape !== undefined,
          // ...and "Begeleiding: tekst / stem", for a lesson with voice-overs (ADR-035).
          guideToggle: !recorded && !inClass && voicesOf(lesson.steps, media).length > 0,
          // ...and "Spannend: aan/uit" for a lesson with a Spannend part (ADR-043); calm by default.
          ...(!inClass && models && hasSpannend(lesson.steps)
            ? {
                spannendToggle: {
                  spannend,
                  onChange: (on: boolean) => {
                    localSpannend = on;
                  },
                },
              }
            : {}),
        });
  // The desktop preview's corner button ("Klik om geluid te starten", then
  // "Geluid: aan/uit"); `M` does the same. Not in a headset: the card and the
  // Quest's volume buttons are there.
  let removeSoundButton = (): void => {};
  if (soundscape && engine && !stage.presenting) {
    void (navigator.xr?.isSessionSupported('immersive-vr') ?? Promise.resolve(false)).then(
      (headset) => {
        if (headset || !running) return;
        const corner = soundButton(engine, 'corner');
        cornerStack().append(corner.element);
        removeSoundButton = corner.remove;
      },
      () => {},
    );
  }
  // "Spannend: aan/uit" in the desktop preview's corner (ADR-043), outside a class, for a lesson
  // that has a Spannend part; `?spannend=1` turns it on from the start. Not in a headset: the
  // start card asks there.
  let removeSpannendButton = (): void => {};
  if (interactive && !inClass && models && hasSpannend(lesson.steps) && !stage.presenting) {
    void (navigator.xr?.isSessionSupported('immersive-vr') ?? Promise.resolve(false)).then(
      (headset) => {
        if (headset || !running) return;
        const corner = spannendButton(spannend, 'corner', (on) => (localSpannend = on));
        cornerStack().append(corner.element);
        removeSpannendButton = corner.remove;
      },
      () => {},
    );
  }
  applyStep();

  return {
    built,
    ui,
    player,

    /**
     * Put the lesson in front of the viewer.
     *
     * Called when the headset starts presenting rather than when the lesson
     * is built: before that there is no head pose to be in front of, and a
     * lesson placed against the desktop preview's camera lands somewhere
     * arbitrary the moment a student enters AR.
     */
    arrive(): void {
      placeable?.arrive();
    },

    presentingChanged: checkEntry,

    setGuide,

    refresh(fresh, freshCues, freshVariants): void {
      // In place: the voice player, the plate and the 360° photos read these maps as they go.
      for (const id of Object.keys(media)) if (fresh[id] === undefined) delete media[id];
      Object.assign(media, fresh);
      for (const id of Object.keys(variants)) {
        if (!Object.hasOwn(freshVariants, id)) delete variants[id];
      }
      Object.assign(variants, freshVariants);
      for (const id of Object.keys(cues)) if (freshCues[id] === undefined) delete cues[id];
      Object.assign(cues, freshCues);
      // A step's paging is worked out again from the cues as they now are.
      paged.clear();
      voiceChanged();
    },

    loaded: () => sceneLoaded && photosSettled,

    setSpannend(on: boolean): void {
      spannend.set(on);
      if (!firstWaiting) return;
      firstWaiting = false;
      if (sceneIndex === 0) models?.enterStep(0, { forward: true, spannend: spannend.enter() });
    },

    setWorldStates: (states: ReadonlyMap<string, string>): void => setWorldStates(states, true),

    setPaused(paused: boolean): void {
      windowPaused = paused;
    },

    picture: {
      ready: () =>
        Promise.all([
          look.ready,
          built.photos.then(
            () => undefined,
            () => undefined,
          ),
          models?.loaded ?? Promise.resolve(),
        ]).then(() => undefined),
      changeSeconds: (index) => MOTION_TIMING.still + changeSeconds(index),
      problems: (index) => [
        // A world lesson in the studio: its package refused, or no code world either.
        ...(lesson.scene.type === 'world' && !source ? ['world'] : []),
        ...(look.missing() > 0 ? ['world-files'] : []),
        ...(photosFailed > 0 ? ['photos'] : []),
        ...((models?.missing ?? 0) > 0 ? ['models'] : []),
        // A 360° photo that failed (or was never chosen): the studio stands in for it.
        ...(look.pano && !look.pano.showsPhotoAt(index) ? ['pano'] : []),
      ],
      hide: () => {
        const shown = [
          badges.group,
          ...[...built.objects.values()].map((object) => object.label),
        ].filter((object) => object.visible);
        for (const object of shown) object.visible = false;
        return () => {
          for (const object of shown) object.visible = true;
        };
      },
    },

    dispose(): void {
      // The lesson's own things, and nothing of the stage's: the renderer, the
      // canvas and any XR session running on it outlive this.
      setLessonRunning(false);
      running = false;
      stopFrames();
      models?.dispose();
      removeSpannendButton();
      look.dispose();
      // In the fade to black; the engine, its context and its switches stay.
      if (soundscape && audio?.soundscape === soundscape) audio.stop();
      // The voice with it: stopped, its files let go.
      stopFollowingVoice();
      stopWaitingForSound();
      pacer.dispose();
      autoplay?.dispose();
      cancelStall();
      voice?.dispose();
      removeSoundButton();
      perf?.dispose();
      removeXrButton();
      removeShapeNote();
      window.removeEventListener('keydown', onKey);
      stopPlacing();
      for (const bench of benches.values()) bench.dispose();
      // The grab went to this lesson's furniture: nothing may reach it once it is gone.
      if (furnishing) stage.offGrab();
      furnishing?.dispose();
      for (const lock of locks) lock.dispose();
      for (const toolbar of toolbars) toolbar.dispose();
      ui.dispose();
      frameAids?.dispose();
      badges.dispose();
      measure?.dispose();
      surfaces?.dispose();
      built.dispose();
      built.root.removeFromParent();
    },
  };
}

/** A teleport's fade in (docs/04: about 0.3 s), after a black held for a late 360° photo. */
const TELEPORT_SECONDS = 0.3;

/** The villa's furniture as a lesson runs it (ADR-037): made by `run` for a villa lesson in VR. */
type Furnishing = {
  readonly view: FurnitureView;
  readonly lectern: FurnishLectern;
  readonly interaction: FurnishInteraction;
  readonly notice: NoticePlate;
  dispose(): void;
};

/**
 * Sets a world with a placement space up for furnishing (the villa, or a package with a plaatsruimte): the set's pieces from their signed URLs, the side lectern's
 * controls, the refusal plate, and the interaction between them and the link -- the session's in a
 * class, a LocalFurnishing otherwise. The pupil starts at the space's first viewpoint, under the opening's
 * black, where every piece starts.
 */
function furnishWorld(input: {
  stage: Stage;
  ui: LessonUi;
  player: LessonPlayer;
  media: Readonly<Record<string, string>>;
  /** Where pupils' work may stand: the villa's plan, or the package's plaatsruimte (V10). */
  space: PlacementSpace;
  furniture: RunOptions['furniture'];
  clock: () => number;
  putNote: (text: string | undefined) => void;
}): Furnishing {
  const { stage, ui, player, media, space } = input;
  const pieces = input.furniture?.set?.pieces ?? [];
  const link = input.furniture?.link ?? new LocalFurnishing(pieces, space);
  const view = new FurnitureView({
    fetchBytes,
    space,
    onMissing: (missing) =>
      input.putNote(`Meubels: ${missing} van de ${pieces.length} niet geladen`),
  });
  stage.scene.add(view.root);
  const notice = new NoticePlate();
  stage.scene.add(notice.mesh);
  const lectern = new FurnishLectern(ui.group, space);
  const raycaster = new THREE.Raycaster();
  const uiAlong = (ray: THREE.Ray): number | undefined => {
    raycaster.set(ray.origin, ray.direction);
    raycaster.far = Infinity;
    return raycaster.intersectObjects(
      [...ui.pickables, ui.panelMesh, ...lectern.pickables],
      false,
    )[0]?.distance;
  };
  const roomView = (room: string) => {
    const { pos, look } = (
      space.rooms.find((candidate) => candidate.id === room) ?? space.rooms[0]!
    ).view;
    return {
      position: [pos[0], stage.eyeHeight, pos[2]] as const,
      lookAt: look,
      floor: [pos[0], 0, pos[2]] as const,
    };
  };
  const eye = new THREE.Vector3();
  const interaction = new FurnishInteraction({
    link,
    view,
    lectern,
    pieces,
    media,
    space,
    now: () => performance.now(),
    rig: () => ({ at: [stage.rig.position.x, stage.rig.position.z], yaw: stage.rig.rotation.y }),
    eye: () => {
      stage.activeCamera.getWorldPosition(eye);
      return [eye.x, eye.y, eye.z];
    },
    travel: (arrive) => stage.travelTo(arrive),
    travelling: () => stage.travelling,
    placeRoom: (room) => stage.placeRig(roomView(room)),
    redrawShadow: () => stage.redrawShadow(),
    notice: (text, id) => {
      const pose = view.pose(id);
      const info = view.info(id);
      if (!pose || !info) return;
      notice.show(text, new THREE.Vector3(pose.at[0], info.size[1], pose.at[1]), input.clock());
    },
    active: () => player.state.step.task?.type === 'furnish',
    uiAlong,
  });
  // Where every piece starts: the lesson opens in the space's first room (the villa's garage).
  stage.placeRig(roomView(space.begin));
  return {
    view,
    lectern,
    interaction,
    notice,
    dispose() {
      interaction.dispose();
      view.dispose();
      lectern.dispose();
      notice.dispose();
    },
  };
}

const formatMetres = (metres: number): string =>
  metres < 1 ? `${Math.round(metres * 100)} cm` : `${metres.toFixed(2).replace('.', ',')} m`;

/**
 * Starts the right kind of XR session. Written by hand rather than using
 * three.js's VRButton/ARButton so the copy is Dutch and one button covers both
 * modes; the session options are the same ones those helpers use.
 */
/**
 * Offers the way into the headset, and takes the offer away once it is taken.
 *
 * WebXR will only start an immersive session from a user gesture, so a lesson
 * pushed by a teacher cannot put thirty headsets into VR by itself however
 * much we would like it to. One tap is the floor, and this exists to make that
 * tap as close to unmissable as the platform allows: a card in the middle of
 * the view rather than a button in a corner, because a child wearing a headset
 * should not have to hunt for it.
 *
 * Only once per class, though -- the stage survives a lesson ending, so a
 * second lesson arrives without anybody tapping anything.
 *
 * Falls back to the old corner button where there is no headset, which is the
 * desktop preview and is not worth a full-screen card.
 */
function addXrButton(
  stage: Stage,
  lesson: Lesson,
  // Both off for a lesson played from a preview ticket (ADR-029). Separate so
  // the name can be asked there after all by changing one argument.
  {
    askName = true,
    reportTrouble = true,
    soundToggle = false,
    guideToggle = false,
    spannendToggle,
  }: {
    askName?: boolean;
    reportTrouble?: boolean;
    soundToggle?: boolean;
    guideToggle?: boolean;
    spannendToggle?: { spannend: SpannendSwitch; onChange: (on: boolean) => void };
  } = {},
): () => void {
  const xr = navigator.xr;
  const isAr = lesson.mode === 'ar';
  const mode: XRSessionMode = isAr ? 'immersive-ar' : 'immersive-vr';

  let teardown = (): void => {};
  const remove = (): void => teardown();

  if (!xr) {
    teardown = cornerNotice('Desktopvoorbeeld (geen WebXR)');
    return remove;
  }

  void xr.isSessionSupported(mode).then((supported) => {
    if (!supported) {
      teardown = cornerNotice('Desktopvoorbeeld (geen headset)');
      return;
    }

    const card = document.createElement('div');
    card.className = 'card';
    card.innerHTML = '<h1></h1><p></p>';
    card.querySelector('h1')!.textContent = lesson.title;
    card.querySelector('p')!.textContent = askName
      ? 'Zet je headset goed op, typ je naam en druk op de knop om te beginnen.'
      : 'Zet de headset goed op en druk op de knop om te beginnen.';

    // Asked for here rather than on the waiting screen: this is the one moment
    // the child who is about to do the lesson is certainly the one looking at
    // the screen. It starts empty every time -- a headset is shared, and a
    // field pre-filled with the last child's name is worse than no name at
    // all, because the teacher's grid would state it with confidence.
    //
    // Not for a lesson played from a preview ticket: nothing it does is
    // recorded, so a name would be asked for nothing (ADR-029).
    let input: HTMLInputElement | undefined;
    if (askName) {
      const nameRow = document.createElement('div');
      nameRow.className = 'namebox';
      const label = document.createElement('label');
      label.htmlFor = 'student-name';
      label.textContent = 'Wie ben jij?';
      input = document.createElement('input');
      input.id = 'student-name';
      input.type = 'text';
      input.autocomplete = 'off';
      input.maxLength = STUDENT_NAME_MAX;
      input.placeholder = 'Je naam';
      nameRow.append(label, input);
      card.append(nameRow);
    }
    const name = input;

    const enter = document.createElement('button');
    enter.className = 'lesson';
    enter.type = 'button';
    enter.textContent = isAr ? 'Start in AR' : 'Start in VR';
    card.append(enter);
    // Chosen before entering: inside, the Quest's volume buttons do the rest.
    let removeToggle = (): void => {};
    if (soundToggle && audio) {
      const toggle = soundButton(audio, 'card');
      card.append(toggle.element);
      removeToggle = toggle.remove;
    }
    // Under "Geluid": text or voice as the guide (ADR-035), preset from the address or the build.
    if (guideToggle) {
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'lesson sound-toggle guide-toggle';
      const show = (): void => {
        toggle.textContent = guideLabel(localGuide);
        toggle.setAttribute('aria-pressed', String(localGuide === 'voice'));
      };
      toggle.addEventListener('click', (event) => {
        event.stopPropagation();
        localGuide = localGuide === 'voice' ? 'text' : 'voice';
        current?.setGuide(localGuide);
        show();
      });
      show();
      card.append(toggle);
    }
    // "Spannend: aan/uit" (ADR-043): asked here before entering, calm by default.
    if (spannendToggle) {
      const toggle = spannendButton(spannendToggle.spannend, 'card', spannendToggle.onChange);
      card.append(toggle.element);
    }
    overlay.append(card);

    // No name, no lesson. Disabled rather than refused on tap: a button that
    // does nothing when pressed reads as a broken headset to a ten-year-old,
    // and there is no teacher standing next to every one of them.
    if (name) {
      const gate = (): void => {
        enter.disabled = !hasStudentName(name.value);
      };
      name.addEventListener('input', gate);
      gate();
    }

    teardown = (): void => {
      removeToggle();
      card.remove();
    };

    enter.addEventListener('click', () => {
      // The same tap lets the sound play, before requestSession takes the gesture.
      audio?.unlock();
      enter.disabled = true;
      enter.textContent = 'Een moment…';
      // Said before the headset goes dark, so the teacher's grid has the name
      // by the time the tile turns "in de les".
      if (name) session?.named(name.value);

      void xr
        .requestSession(mode, {
          requiredFeatures: isAr ? ['hit-test', 'local-floor'] : ['local-floor'],
          optionalFeatures: ['hand-tracking'],
        })
        .then(async (session) => {
          removeToggle();
          card.remove();
          // Behind the system menu: faded, still running; the headset off: suspended.
          if (audio) followXrVisibility(session, audio);
          await stage.enterXr(session, mode);
        })
        .catch((error: unknown) => {
          // Refused, or the headset was busy. Say so and let them try again
          // rather than leaving a dead button in front of a class.
          console.warn('could not enter the headset session', error);
          // A headset that will not enter VR is the hardest failure to
          // diagnose down a phone line, and the one the headset itself is
          // certain about. Not from a played preview, though: that is a
          // person looking at a lesson, not a school's headset failing, and
          // it would travel with this browser's next check-in as one.
          if (reportTrouble) rememberTrouble('xr-failed', (error as Error)?.name);
          enter.disabled = false;
          enter.textContent = isAr ? 'Start in AR' : 'Start in VR';
        });
    });
  });

  return remove;
}

/**
 * A note in a top corner of the desktop preview: that there is no headset to
 * enter (right), or that shapes were drawn simpler (left). DOM, so it is never
 * seen inside a headset.
 */
function cornerNotice(text: string, corner: 'left' | 'right' = 'right'): () => void {
  const button = document.createElement('button');
  button.className = 'lesson';
  button.type = 'button';
  button.disabled = true;
  button.textContent = text;
  // At the top, clear of the lectern buttons at the bottom of the 3D view;
  // on the left, below the step counter (#hud) that already sits there, and
  // never wider than its half of the window, wrapping rather than running
  // under the note in the other corner.
  if (corner === 'right') {
    // Stacked with the sound button, with room between (index.html, .corner-stack).
    cornerStack().append(button);
    return () => button.remove();
  }
  button.style.cssText =
    'position:fixed;left:16px;top:52px;width:auto;justify-content:center;' +
    'min-width:0;max-width:calc(50vw - 32px);white-space:normal;text-align:left';
  document.body.append(button);
  return () => button.remove();
}

/** The desktop preview's top-right corner, where its notice and sound button stack. */
function cornerStack(): HTMLElement {
  let stack = document.querySelector<HTMLElement>('.corner-stack');
  if (!stack) {
    stack = document.createElement('div');
    stack.className = 'corner-stack';
    document.body.append(stack);
  }
  return stack;
}

// Read before anything else, so the ticket is off the address at once
// (takePlayRequest), and so a play tab never registers the service worker: it
// needs neither offline mode nor updates, and an update applied at "Klaar ✓"
// -- or while the lesson loads -- would reload the tab onto "Deze link werkt
// niet meer", its ticket spent. A service worker already controlling this
// origin still serves the page; without registerSW here it never reloads it.
//
// A demo build (vite.config.ts) is none of the above: it plays its one lesson
// and does nothing else. Every other build has no demo lesson (`null`).
const demoWorldsCarried = demoWorlds(demoWorldsBuild);
const demo = demoBuild === null ? undefined : demoLesson(demoBuild, demoWorldsCarried);
const play = demo ? undefined : takePlayRequest();
// The console's viewer of a meubelset (ADR-037): like a played lesson, no service worker, no
// pairing, no socket; its ticket off the address at once.
const viewer = demo || play ? undefined : takeViewerRequest();
// The console's viewer of a library model (ADR-043, contract C10): the same, its own ticket.
const modelViewer = demo || play || viewer ? undefined : takeModelViewerRequest();
// The console's viewer of a world (part 3b): the same, its own ticket.
const worldViewer = demo || play || viewer || modelViewer ? undefined : takeWorldViewerRequest();
// A lesson's own picture (ADR-098): the same, its own ticket.
const lessonPicture =
  demo || play || viewer || modelViewer || worldViewer ? undefined : takeLessonPictureRequest();
// A test drive of a library item (ADR-048): the same, its own ticket.
const testDrive =
  demo || play || viewer || modelViewer || worldViewer || lessonPicture
    ? undefined
    : takeTestDriveRequest();
// `?pakket=<url of wereld.json>`: a world package opened from a folder (the kit's `wereld bekijk`).
const pakket =
  demo || play || viewer || modelViewer || worldViewer || lessonPicture || testDrive
    ? undefined
    : new URLSearchParams(location.search).get('pakket');
// `?bekijken=fixture:wereld|fixture:rex|fixture:les` (dev only): Bekijken mode, or Steekproef's
// lesson preview, without an API (ADR-058).
const bekijkenFixture =
  import.meta.env.DEV &&
  !play &&
  !demo &&
  !viewer &&
  !modelViewer &&
  !worldViewer &&
  !lessonPicture &&
  !testDrive &&
  !pakket
    ? new URLSearchParams(location.search).get('bekijken')
    : null;
// A package preview (?pakket=) is a developer's look at a folder: no service worker caching it either.
if (
  !play &&
  !demo &&
  !viewer &&
  !modelViewer &&
  !worldViewer &&
  !lessonPicture &&
  !testDrive &&
  !pakket
)
  registerServiceWorker((status) => console.info('service worker:', status));

/**
 * A paired headset goes straight to its lessons and confirms its token in the
 * background. It is not held at a spinner waiting for the network: a lesson
 * must start in a room with no wifi. Only the API's `device-revoked` -- the
 * pairing was revoked -- sends it back to the pairing screen (unpairHere).
 */
async function boot(): Promise<void> {
  startAudio('class');
  const identity = storedIdentity();
  if (!identity) {
    showPairing();
    return;
  }

  showPicker();
  linkSession();

  // World files the manifest no longer names leave the cache (ADR-032).
  void sweepWorldCache();

  // Not awaited: the lobby is already up, and a storage guarantee is worth
  // nothing compared to a lesson starting. Asked on every boot rather than
  // once at pairing, because a browser that refused while the headset was
  // new may grant it later -- Chromium's heuristics improve as an installed
  // app is actually used.
  void keepPairingOnDevice().then((outcome) => {
    if (outcome === 'refused') {
      // Worth reporting, not just logging: a headset whose storage can be
      // evicted is one that will one day appear to have unpaired itself, and
      // this is the only warning anybody gets beforehand.
      console.info('storage is not persistent; this pairing could be evicted');
      rememberTrouble('storage-refused');
    }
  });

  await checkInHere(identity);
}

/**
 * Checks in (at boot, and right after pairing): the headset's name, its id, and whether it is one of
 * the platform's test headsets (ADR-058). The lobby fetches for its lessons only once this answered.
 */
async function checkInHere(
  identity: NonNullable<ReturnType<typeof storedIdentity>>,
): Promise<void> {
  const battery = await batteryLevel();
  const result = await checkIn(identity, {
    appVersion: __APP_VERSION__,
    ...(battery === undefined ? {} : { battery }),
  });

  if (result.status === 'ok') {
    // Answers "which one are you?", which a teacher asks out loud in every
    // classroom with more than three headsets in it.
    deviceName = result.name;
    platformHeadset = result.platform === true;
    // Who this headset is in the furnishing messages (ADR-037), until a session.assigned says it.
    if (result.deviceId !== undefined) session?.knowDevice(result.deviceId);
  }
  if (result.status !== 'revoked') {
    checkedIn = true;
    if (overlay.querySelector('.waiting')) renderWaiting();
  }

  // `revoked`: unpaired already, by unpairHere, as any request or the socket hearing it would.
}

/**
 * The pairing ended: unlinked in the console, or the school removed (docs/06,
 * "What a headset keeps after unpairing"). Whichever request or socket heard
 * it -- the check-in at boot, a ticket, a lesson, the hub's `device-revoked`
 * in the middle of a class -- pairing-client's `revoked` runs this, once,
 * right after the token is gone and while the school's caches are deleted.
 *
 * The socket closes first, so nothing new is handed out; a lesson running is
 * taken down quietly (its voice and sound stop, the headset leaves VR -- the
 * pairing screen is DOM, which cannot be seen from inside an XR session, and
 * a student left in a dead lesson would have to find the way out alone); the
 * prefetch settles; the school's data is cleared again behind it; then the
 * pairing screen.
 */
async function unpairHere(): Promise<void> {
  console.info('pairing revoked; dropping the school data and returning to the pairing screen');
  endBekijken();
  headsetMode.reset();
  // Before the lesson comes down, which draws the lobby for a moment: no "Zelf oefenen" on it,
  // and no name of the school's. The queue's copy in memory would write the results back.
  resultQueue.clear();
  deviceName = undefined;
  platformHeadset = false;
  checkedIn = false;
  freeTimeAllowed = false;
  currentSessionId = undefined;
  assigned = undefined;
  remoteStep = undefined;
  linked = false;
  try {
    await sessionFlow.revoked();
  } finally {
    // Whatever failed on the way, a tokenless headset shows the way back to a school.
    showPairing();
    sweepAfterUnpair();
  }
}

/**
 * Clears the school's caches again a little later. A request the lesson made just before the
 * unlink -- a photo, a voice-over, a lesson body -- is not aborted, and the service worker caches
 * its answer when it lands, recreating a cache already cleared. Only while still unpaired: a new
 * pairing cleared them itself before storing its token (pairDevice).
 */
function sweepAfterUnpair(): void {
  for (const ms of [10_000, 60_000]) {
    setTimeout(() => {
      if (!storedIdentity()) void forgetSchoolData();
    }, ms);
  }
}

whenRevoked(unpairHere);

/**
 * The teacher's view of what the class is looking at.
 *
 * Same engine, same lesson, driven by the same session state -- rendered into
 * an iframe on the console rather than into a headset. A second renderer
 * written to approximate this one would drift from it, and a teacher
 * describing a step the class cannot see is worse than no preview.
 *
 * Nothing here can steer: the preview sends no messages, has no result queue
 * behind it, and never offers a way into VR.
 */
function bootPreview(request: { sessionId: string; ticket: string }): void {
  document.body.classList.add('preview');
  showWaiting('Wachten op de les…');

  const preview = startPreview(request);

  type PreviewState = Parameters<Parameters<typeof preview.onState>[0]>[0];
  // The class's state as it last was: a served package's world takes a moment to be ready, and
  // the state that came meanwhile is put on the lesson when it runs.
  let latest: PreviewState | undefined;
  const follow = (state: PreviewState): void => {
    const stepId = state.stepId;
    if (!current || typeof stepId !== 'string') return;
    // Always the class's step, and always locked, whatever the class's mode:
    // this is a window onto the lesson the teacher is steering, not a second
    // pair of hands in it. In a free class it shows where the teacher is,
    // which is what they are talking from.
    remoteStep = stepId;
    // The class's Spannend, from the step it next enters (ADR-043).
    current.setSpannend(spannendOf(state));
    current.setPaused(state.paused === true);
    if (current.player.state.step.id !== stepId) current.player.goto(stepId);
    current.player.setControl('locked');
  };

  const views = new Generation();
  preview.onAssigned((assignment) => {
    overlay.innerHTML = '';
    const ticket = views.begin();
    void chooseWorld(assignment.lesson, assignment.worlds, assignment.media).then((choice) => {
      // Ended, or another lesson assigned, meanwhile: this one is not shown.
      if (!views.current(ticket)) return;
      current?.dispose();
      current = run(assignment.lesson, {
        interactive: false,
        ...choice,
        media: assignment.media,
        variants: assignment.variants,
        // The models the class sees, walking as theirs do (ADR-043).
        ...(assignment.models ? { models: assignment.models } : {}),
      });
      if (latest) follow(latest);
    });
  });

  preview.onState((state) => {
    latest = state;
    follow(state);
  });

  preview.onEnded(() => {
    views.leave();
    current?.dispose();
    current = undefined;
    if (stage) stage.visible = false;
    hud.textContent = '';
    showWaiting('De les is beëindigd.');
  });

  preview.onTrouble((message) => {
    if (current) return;
    showWaiting(message);
  });
}

/**
 * One lesson played alone, from a preview ticket (`?play=<ticket>`, ADR-029):
 * the platform admin looking at a generated lesson before any school sees it,
 * and a teacher looking at one of the school's before a class does.
 *
 * The ordinary player, so what is seen is what a class would see -- desktop
 * preview on a laptop, VR or AR in the Quest browser -- but outside any
 * school's life: no pairing screen, no check-in, no session, no results and
 * no name asked. A pairing this browser already holds is left alone.
 */
async function bootPlay(request: PlayStart): Promise<void> {
  startAudio('play');
  // A reload of a tab whose ticket is already spent: the address no longer
  // carries it (takePlayRequest), and asking again would only be refused.
  if (request.kind === 'spent') {
    showPlayCard(playProblemText({ kind: 'gone' }));
    return;
  }
  showWaiting(PLAY_TEXT.loading);
  const outcome = await fetchPlayLesson(request.ticket);
  if (outcome.kind !== 'ready') {
    showPlayCard(playProblemText(outcome));
    return;
  }
  void evictUnsigned(outcome.loaded.lesson, outcome.loaded.media, outcome.loaded.variants);
  const set = outcome.furniture;
  // The desktop preview's villa (dev only): a hub in the page that decides who holds what and runs
  // one other pupil, so one browser sees a class furnish. Never in a build.
  let makeLink: (() => FurnishingLink) | undefined;
  if (import.meta.env.DEV && set && request.ticket.startsWith('fixture:')) {
    const { FakeHub } = await import('./dev/fake-hub.js');
    // The fixture is the villa's lesson (`fixture:villa`).
    makeLink = () => new FakeHub(set.pieces, VILLA_SPACE);
  }
  const world = await chooseWorld(
    outcome.loaded.lesson,
    outcome.loaded.worlds,
    outcome.loaded.media,
  );
  playLesson(outcome.loaded.lesson, {
    world,
    media: outcome.loaded.media,
    variants: outcome.loaded.variants,
    cues: outcome.loaded.cues,
    models: outcome.loaded.models,
    ...(set ? { furniture: { set, ...(makeLink ? { makeLink } : {}) } } : {}),
  });
}

/** What a played lesson says at its end and when it fails: a ticket's, or a demo's. */
type PlayWords = {
  ended: { title: string; body: string };
  again: string;
  failed: (detail: string) => { title: string; body: string };
  /** What "again" does when it is not simply the same lesson once more (a test drive's view, ADR-048). */
  restart?: () => void;
  /** Called when the lesson's last step is over, once the lesson is gone (a test drive's view, ADR-048). */
  onEnd?: () => void;
};

const TICKET_WORDS: PlayWords = {
  ended: PLAY_TEXT.ended,
  again: PLAY_TEXT.again,
  failed: (detail) => playProblemText({ kind: 'failed', detail }),
};

/** A played lesson's or the demo's media map and subtitle cues, and a villa lesson's furniture. */
type PlayMedia = {
  media: Readonly<Record<string, string>>;
  /** A ticket's 360° photos' cubes (ADR-041); the demo has none. */
  variants?: Readonly<PanoVariants>;
  cues: CueMap;
  furniture?: { set: FurnitureAssigned; makeLink?: () => FurnishingLink };
  /** The library models the lesson names (ADR-043): a ticket's, or the demo's own files. */
  models?: ModelCatalogue;
  /** A world lesson's world, chosen before it runs (chooseWorld): a served package, or the code's. */
  world?: { world?: WorldSource | null; worldNote?: string };
};

function playLesson(
  lesson: Lesson,
  media: PlayMedia,
  words: PlayWords = TICKET_WORDS,
  kind: 'play' | 'demo' = 'play',
): void {
  overlay.innerHTML = '';
  current?.dispose();
  current = undefined;
  try {
    current = run(lesson, {
      recorded: false,
      // Once the press has been handled: "Klaar ✓" redraws its own panel after
      // this returns, and that panel has to still exist when it does.
      media: media.media,
      ...(media.variants ? { variants: media.variants } : {}),
      cues: media.cues,
      ...(media.models ? { models: media.models } : {}),
      ...(media.world ?? {}),
      // The viewer's guide: `?guide=`, the demo's build, the start card, G (ADR-035).
      guide: localGuide,
      voiceOn: voiceStartsOn(kind),
      // A demo built with `--autoplay` plays itself to its end card.
      ...(kind === 'demo' && demoPlay.autoplay ? { autoplay: true } : {}),
      // Played alone, a villa lesson grants every grab (LocalFurnishing) or runs the dev hub.
      ...(media.furniture
        ? {
            furniture: {
              set: media.furniture.set,
              ...(media.furniture.makeLink ? { link: media.furniture.makeLink() } : {}),
            },
          }
        : {}),
      onFinish: () => window.setTimeout(() => void finishPlay(lesson, media, words, kind), 0),
    });
  } catch (error) {
    // A lesson that validates can still be one the renderer chokes on -- a
    // generated one especially, which is what this mode is for. Say so rather
    // than leave a blank tab, and nothing of it is reported: there is no
    // school here to report it to.
    if (stage) stage.visible = false;
    hud.textContent = '';
    showPlayCard(words.failed((error as Error).message));
    return;
  }
  setLessonRunning(true);
}

/** The end of a played lesson: out of the headset, and the offer to see it again. */
async function finishPlay(
  lesson: Lesson,
  media: PlayMedia,
  words: PlayWords,
  kind: 'play' | 'demo',
): Promise<void> {
  current?.dispose();
  current = undefined;
  hud.textContent = '';
  words.onEnd?.();
  // The same lesson again needs no second ticket: it is already here.
  showPlayCard(
    words.ended,
    {
      label: words.again,
      onClick: words.restart ?? (() => playLesson(lesson, media, words, kind)),
    },
    endCredits(lesson, media.models ?? {}, media.world),
  );

  // As at the end of a class (endLesson): leave immersive mode first, and only
  // then hide the canvas -- a hidden canvas cannot present, and the view in
  // the headset would freeze instead of returning.
  await stage?.exitXr();
  if (stage) stage.visible = false;
}

/**
 * A demo build's one lesson (`pnpm demo:build`, src/demo.ts): played as a
 * lesson from a ticket is, with the lesson taken from the build instead of
 * fetched. Nothing else of the app runs -- no service worker, no pairing, no
 * check-in, no socket -- and nothing is fetched but the page's own folder.
 */
async function bootDemo(outcome: DemoOutcome): Promise<void> {
  startAudio('demo');
  if (outcome.kind !== 'ready') {
    showPlayCard(demoProblemText(outcome.detail));
    return;
  }
  // Its voice-overs are files beside the page (`pnpm demo:build`), and
  // Voorlezen starts on: every step reads itself after "Start in VR".
  // Its cues too (`--guide` presets the start card to voice, ADR-035); only valid ones.
  // Its library models too (`pnpm demo:build`, ADR-043): their GLBs and sounds are files beside
  // the page, named in the same media map.
  // Its world's package too (`--werelden`): chosen as a served lesson's is, its files in the same map.
  const world = await chooseWorld(outcome.lesson, demoWorldsCarried, demoMedia);
  playLesson(
    outcome.lesson,
    { media: demoMedia, cues: cuesFrom(demoCues), models: demoModels(demoModelsBuild), world },
    { ...DEMO_TEXT, failed: demoProblemText },
    'demo',
  );
}

/** "Bronnen: …" under a played lesson's end (ADR-043), or nothing when there is nothing to credit. */
function endCredits(
  lesson: Lesson,
  models: ModelCatalogue,
  world: PlayMedia['world'],
): string | undefined {
  const source = worldSourceOf(lesson, world?.world);
  const plan = soundPlanFor(lesson, source);
  const credits = lessonCredits(
    lesson,
    models,
    plan ? recordingsOf(plan, source?.manifest) : [],
    source?.credits,
  );
  return credits.length === 0 ? undefined : `${CREDITS_TEXT.title}: ${creditsText(credits)}`;
}

/** A card for a played lesson: a heading, a line, at most one thing to do, and its sources. */
function showPlayCard(
  text: { title: string; body: string },
  action?: { label: string; onClick: () => void },
  footnote?: string,
): void {
  overlay.innerHTML = '';
  const card = document.createElement('div');
  card.className = 'card';
  card.innerHTML = '<h1></h1><p></p>';
  card.querySelector('h1')!.textContent = text.title;
  card.querySelector('p')!.textContent = text.body;
  if (action) {
    const button = document.createElement('button');
    button.className = 'lesson';
    button.type = 'button';
    button.textContent = action.label;
    button.addEventListener('click', action.onClick);
    card.append(button);
  }
  if (footnote) {
    const small = document.createElement('p');
    small.className = 'hint';
    small.textContent = footnote;
    card.append(small);
  }
  overlay.append(card);
}

/**
 * The console's viewer of a meubelset (`?viewer=<ticket>`, ADR-037): the set's pieces drawn by the
 * headset's own renderer, one turnable on a grey floor beside a 1.5 m figure, their thumbnails, and
 * a saved arrangement as a floor plan and four photos -- all posted to the console that asked, and
 * only to it. No sound, no XR, nothing sent to the API but the ticket.
 */
async function bootViewer(request: { ticket: string }): Promise<void> {
  document.body.classList.add('preview');
  showWaiting('De meubels worden geladen…');
  const outcome = await fetchViewer(request.ticket);
  if (outcome.kind !== 'ready') {
    // Nobody to tell but the page: the console's origin is what the ticket would have named.
    showWaiting(outcome.kind === 'gone' ? VIEWER_TEXT.gone : VIEWER_TEXT.failed);
    return;
  }
  const { answer } = outcome;
  const { ViewerScene } = await import('./render/furniture/viewer-scene.js');
  overlay.innerHTML = '';
  const scene = new ViewerScene(document.body);
  // The dev harness checks the piece keeps being drawn after an image; never built in.
  if (import.meta.env.DEV) Object.assign(window, { __viewerScene: scene });
  const post = (message: ViewerMessage): void => {
    if (window.parent !== window) window.parent.postMessage(message, answer.consoleOrigin);
  };
  const host = new ViewerHost({ answer, renderer: scene, post });
  window.addEventListener('message', (event) => {
    if (event.source !== window.parent) return;
    void host.receive({ origin: event.origin, data: event.data as unknown });
  });
  await host.start();
}

/**
 * The console's viewer of a library model (`?modelViewer=<ticket>`, ADR-043): the model drawn by
 * the headset's own loader at real size beside a 1.5 m figure, turnable, a clip playing when
 * asked, and its thumbnail -- posted to the console that asked, and only to it. No sound, no XR.
 */
async function bootModelViewer(request: { ticket: string }): Promise<void> {
  document.body.classList.add('preview');
  showWaiting(MODEL_VIEWER_TEXT.loading);
  const outcome = await fetchModelViewer(request.ticket);
  if (outcome.kind !== 'ready') {
    showWaiting(outcome.kind === 'gone' ? MODEL_VIEWER_TEXT.gone : MODEL_VIEWER_TEXT.failed);
    return;
  }
  const { answer } = outcome;
  const { ModelViewerScene } = await import('./render/models/viewer-scene.js');
  overlay.innerHTML = '';
  const scene = new ModelViewerScene(document.body);
  const post = (message: ModelViewerMessage): void => {
    if (window.parent !== window) window.parent.postMessage(message, answer.consoleOrigin);
  };
  const host = new ModelViewerHost({ answer, renderer: scene, post });
  window.addEventListener('message', (event) => {
    if (event.source !== window.parent) return;
    void host.receive({ origin: event.origin, data: event.data as unknown });
  });
  await host.start();
}

/**
 * The console's viewer of a world (`?worldViewer=<ticket>`, part 3b): the world built as a headset
 * builds it, its stage's first view drawn on request as a 960 × 540 JPEG -- posted to the console that
 * asked, and only to it. No sound, no XR.
 */
async function bootWorldViewer(request: WorldViewerStart): Promise<void> {
  document.body.classList.add('preview');
  // Said to the console that embeds this page: the ticket's own origin once it is known, else the ancestor's.
  const tell = (text: string, origin: string | undefined): void => {
    showWaiting(text);
    if (origin && window.parent !== window)
      window.parent.postMessage({ type: 'trouble', text } satisfies WorldViewerMessage, origin);
  };
  if (request.kind === 'spent') {
    tell(WORLD_VIEWER_TEXT.gone, parentOrigin());
    return;
  }
  showWaiting(WORLD_VIEWER_TEXT.loading);
  const outcome = await fetchWorldViewer(request.ticket);
  if (outcome.kind !== 'ready') {
    tell(
      outcome.kind === 'gone' ? WORLD_VIEWER_TEXT.gone : WORLD_VIEWER_TEXT.failed,
      parentOrigin(),
    );
    return;
  }
  const { answer } = outcome;
  const { WorldViewerScene } = await import('./render/worlds/viewer-scene.js');
  overlay.innerHTML = '';
  let scene: InstanceType<typeof WorldViewerScene>;
  try {
    scene = new WorldViewerScene(document.body);
  } catch {
    // No WebGL in this browser.
    tell(WORLD_VIEWER_TEXT.drawing, answer.consoleOrigin);
    return;
  }
  const post = (message: WorldViewerMessage): void => {
    if (window.parent !== window) window.parent.postMessage(message, answer.consoleOrigin);
  };
  const host = new WorldViewerHost({ answer, renderer: scene, post });
  window.addEventListener('message', (event) => {
    if (event.source !== window.parent) return;
    void host.receive({ origin: event.origin, data: event.data as unknown });
  });
  await host.start();
}

/** How long a picture waits, at most, for a fade still held (a 360° photo late at its black). */
const PICTURE_PATIENCE_MS = 8000;

/**
 * A lesson's own picture (`?lessonPicture=<ticket>`, ADR-098): the console's hidden frame (its first
 * picture) or "Kies plaatje". The ordinary player, not interactive, as the teacher's window onto a
 * class runs it; a capture goes to the step -- by way of another step when it can, so the step is
 * put at its calm end at once rather than played -- runs the frames its changes take without
 * drawing them (Stage.settle), and draws one still from where the pupil stands: 1280 × 720 at 60°,
 * without the lectern, the names and the points.
 */
async function bootLessonPicture(request: LessonPictureStart): Promise<void> {
  document.body.classList.add('preview');
  const tell = (text: string, origin: string | undefined): void => {
    showWaiting(text);
    if (origin && window.parent !== window)
      window.parent.postMessage({ type: 'trouble', text } satisfies LessonPictureMessage, origin);
  };
  if (request.kind === 'spent') {
    tell(LESSON_PICTURE_TEXT.gone, parentOrigin());
    return;
  }
  showWaiting(LESSON_PICTURE_TEXT.loading);
  const outcome = await fetchLessonPicture(request.ticket);
  if (outcome.kind !== 'ready') {
    tell(
      outcome.kind === 'gone' ? LESSON_PICTURE_TEXT.gone : LESSON_PICTURE_TEXT.failed,
      parentOrigin(),
    );
    return;
  }
  const { answer } = outcome;
  const { loaded } = answer;
  let suggested: string | undefined;
  const renderer = {
    load: async (): Promise<void> => {
      void evictUnsigned(loaded.lesson, loaded.media, loaded.variants);
      const choice = await chooseWorld(loaded.lesson, loaded.worlds, loaded.media);
      overlay.innerHTML = '';
      current?.dispose();
      current = run(loaded.lesson, {
        interactive: false,
        // Nothing a picture's lesson does is anyone's result.
        recorded: false,
        ...choice,
        media: loaded.media,
        variants: loaded.variants,
        ...(loaded.models ? { models: loaded.models } : {}),
      });
      current.player.setControl('locked');
      await current.picture.ready();
    },
    suggested: (): string =>
      (suggested ??= pictureStep(
        loaded.lesson,
        loaded.models ?? {},
        catalogueOfServed(loaded.worlds ?? {}),
      ).step),
    capture: async (stepId: string): Promise<Blob> => {
      const running = current;
      const theStage = stage;
      if (!running || !theStage) throw new Error('no lesson');
      const steps = loaded.lesson.steps;
      const target = steps.findIndex((step) => step.id === stepId);
      const route = pictureRoute(steps, running.player.state.index, target);
      route.hops.forEach((index, at) => {
        running.player.goto(steps[index]!.id);
        if (at < route.hops.length - 1) theStage.settle(1);
      });
      const seconds = 1.5 + (route.play ? running.picture.changeSeconds(target) : 0);
      theStage.settle(Math.min(seconds, 30));
      // A 360° photo late at its black holds it in real time: wait for it, a little at a time.
      const started = performance.now();
      while (theStage.faded && performance.now() - started < PICTURE_PATIENCE_MS) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        theStage.settle(0.2);
      }
      // Never a picture of a scene that did not load as the lesson has it (ADR-098).
      const problems = running.picture.problems(target);
      if (problems.length > 0) throw new SceneIncomplete(problems);
      const show = running.picture.hide();
      let canvas: HTMLCanvasElement;
      try {
        canvas = theStage.still(LESSON_PICTURE.width, LESSON_PICTURE.height, PICTURE_FRAME.fov);
      } finally {
        show();
      }
      // A frame the browser drew nothing into (a lost WebGL context): one colour, refused.
      if (isBlank(canvas)) throw new SceneIncomplete(['blank']);
      return pictureJpeg(
        (quality) =>
          new Promise<Blob>((resolve, reject) =>
            canvas.toBlob(
              (blob) => (blob ? resolve(blob) : reject(new Error('geen JPEG'))),
              'image/jpeg',
              quality,
            ),
          ),
      );
    },
  };
  const post = (message: LessonPictureMessage): void => {
    if (window.parent !== window) window.parent.postMessage(message, answer.consoleOrigin);
  };
  const host = new LessonPictureHost({ answer, renderer, post });
  window.addEventListener('message', (event) => {
    if (event.source !== window.parent) return;
    void host.receive({ origin: event.origin, data: event.data as unknown });
  });
  await host.start();
}

/**
 * A test drive of a library item (`?testDrive=<ticket>`, ADR-048): each view a small lesson the API
 * built, played by the ordinary player -- desktop preview on a laptop, VR in the Quest browser -- with
 * the 1.70 m figure where the view says, the test panel beside the lectern to change view and play an
 * animation, and in the view alone the item resizable with two hands (render/placeable.ts, as an AR
 * lesson's). Nothing is recorded, and sound follows the page's own mute like any played lesson.
 */
/** A group's pose in the world, as the test panel's placement reads it (yaw about y, tilt about x). */
function worldPoseOf(group: THREE.Object3D): WorldPose {
  group.updateWorldMatrix(true, false);
  const at = group.getWorldPosition(new THREE.Vector3());
  const turned = new THREE.Euler().setFromQuaternion(
    group.getWorldQuaternion(new THREE.Quaternion()),
    'YXZ',
  );
  return { position: [at.x, at.y, at.z], yaw: turned.y, tilt: turned.x };
}

async function bootTestDrive(request: TestDriveStart): Promise<void> {
  startAudio('play');
  // A reload of a tab whose ticket is spent: the address no longer carries it (takeTestDriveRequest).
  if (request.kind === 'spent') {
    showWaiting(TEST_DRIVE_TEXT.gone);
    return;
  }
  showWaiting(TEST_DRIVE_TEXT.loading);
  let answer: TestDriveAnswer;
  if (import.meta.env.DEV && request.ticket.startsWith('fixture:')) {
    // The desktop preview without an API (dev only, never in a build): see dev/test-drive-fixture.ts.
    const fixture = await import('./dev/test-drive-fixture.js');
    if (request.ticket === 'fixture:wereld') {
      const world = await fixture.devWorldTestDrive();
      for (const [url, bytes] of world.files) fixtureFiles.set(url, bytes);
      answer = world.answer;
    } else {
      answer = fixture.devTestDrive();
    }
  } else {
    const outcome = await fetchTestDrive(request.ticket);
    if (outcome.kind !== 'ready') {
      showWaiting(
        outcome.kind === 'gone'
          ? TEST_DRIVE_TEXT.gone
          : `${TEST_DRIVE_TEXT.failed} (${outcome.detail})`,
      );
      return;
    }
    answer = outcome.answer;
  }
  const theStageNow = theStage();
  // The figure, the panel and the resizer, for as long as a view is shown (engine/view-extras.ts).
  const extras = new ViewExtras();

  // The view being shown now: a world package's view waits for its height map, and a press on
  // another view meanwhile wins.
  let shown = 0;
  const show = async (index: number): Promise<void> => {
    extras.dispose();
    const mine = ++shown;
    const view = answer.views[index]!;
    // A world view's world (ADR-049): the served package for "Pakket" or "Huidige versie", its
    // candidate for "Nieuwe versie", the code world for the rest.
    const worldSlug = view.lesson.scene.type === 'world' ? view.lesson.scene.world : undefined;
    const prepared = worldSlug
      ? await prepareWorld(
          worldSlug,
          view.world === 'code' ? {} : view.world === 'nieuw' ? answer.candidate : answer.worlds,
          answer.media,
          fetchBytes,
        )
      : undefined;
    if (mine !== shown) return;
    const problem = prepared?.problems[0];
    if (prepared && problem)
      console.warn(`wereldpakket ongeldig: ${problem.path} ${problem.message}`, prepared.problems);
    playLesson(
      view.lesson,
      {
        media: answer.media,
        variants: answer.variants,
        cues: {},
        models: answer.models,
        ...(prepared
          ? {
              world: {
                world: prepared.source ?? null,
                ...(problem ? { worldNote: `Wereldpakket: ${problem.message}` } : {}),
              },
            }
          : {}),
      },
      {
        ended: TEST_DRIVE_TEXT.ended,
        again: TEST_DRIVE_TEXT.again,
        failed: (detail) => ({ title: TEST_DRIVE_TEXT.failed, body: detail }),
        restart: () => void show(index),
        // The view's last step is over: its figure, panel and resizer go with the lesson.
        onEnd: () => extras.dispose(),
      },
    );
    const now = current;
    if (!now) return;

    let figureAt: [number, number, number] | undefined;
    if (view.figure) {
      figureAt = groundedAt(view.figure.pos, prepared?.source?.definition.ground);
      const figure = makeReferenceFigure(figureAt);
      theStageNow.scene.add(figure);
      extras.add(() => disposeFigure(figure));
    }

    const steps = view.lesson.steps.map((step) => ({ id: step.id, title: step.title }));
    let page = 0;
    let panel: TestPanel | undefined;
    let place: PanelPlace | undefined;
    const hang = (): void => {
      panel?.dispose();
      panel = new TestPanel({ views: answer.views, current: index, steps, page }, (action) => {
        if (action.kind === 'view') void show(action.index);
        else if (action.kind === 'step') pressStep(now.player, action.id);
        else if (action.kind === 'page') {
          page = action.page;
          hang();
        }
      });
      now.ui.group.add(panel.group);
      now.ui.setExtraButtons(panel.pressable);
      place = undefined;
    };
    hang();
    extras.add(() => {
      now.ui.setExtraButtons([]);
      panel?.dispose();
    });

    // The item, as the distance rule's own footprint of it where it stands now (two hands may have
    // scaled it), and the figure: the panel keeps clear of both, on the side away from them.
    const item = now.built.objects.get('item');
    const source = view.lesson.objects?.['item'];
    const slug = source ? modelSlugOf(source.kind) : undefined;
    const facts = slug ? answer.models[slug] : undefined;
    const clips = facts ? (Object.keys(facts.animations) as ModelAnimation[]) : [];
    extras.add(
      theStageNow.onFrame(() => {
        if (!panel) return;
        place = placeTestPanel({
          pose: worldPoseOf(now.ui.group),
          rows: testPanelRows(steps, page),
          obstacles: {
            item:
              item && facts
                ? modelFootprint(
                    facts,
                    [item.group.position.x, item.group.position.z],
                    source?.turn ?? 0,
                    {
                      clips,
                      scale: item.group.scale.x,
                    },
                  )
                : undefined,
            figure: figureAt,
          },
          previous: place,
        });
        panel.group.position.set(place.x, place.y, 0);
      }),
    );

    if (view.scalable && item) {
      // Two pinching hands scale it, as an AR lesson's skeleton (render/placeable.ts); unlocked from the
      // start, and a no-op without hands, so the laptop preview never moves it. Two pinches anywhere
      // count -- a big animal is 16 m away, further than any hand reaches -- so its "inside" is a large
      // box round the rig, and padlock and handles are not part of this view.
      const resizer = new Placeable(item.group, theStageNow, {
        resizable: true,
        bounds: () =>
          new THREE.Box3().setFromCenterAndSize(
            theStageNow.rig.getWorldPosition(new THREE.Vector3()),
            new THREE.Vector3(200, 200, 200),
          ),
      });
      resizer.locked = false;
      extras.add(theStageNow.onFrame(() => void resizer.update(theStageNow.hands)));
    }
  };
  void show(0);
}

/**
 * `?pakket=<url of wereld.json>` (the kit's `wereld bekijk`; ADR-049): a world package read from a
 * folder on this origin and played as a small lesson, a first look and then each state of each
 * feature, so Verder walks through them. Reads same-origin files only (the CSP's `connect-src
 * 'self'`), never the API, and records nothing. A package that is refused shows the studio and says why.
 */
async function bootPackagePreview(address: string): Promise<void> {
  startAudio('play');
  showWaiting('Het wereldpakket wordt geladen…');
  try {
    const preview = await packagePreview(
      address,
      (url) =>
        fetch(url, { cache: 'no-store' }).then((response) => response.json() as Promise<unknown>),
      async (url) => {
        const head = await fetch(url, { method: 'HEAD', cache: 'no-store' });
        const length = Number(head.headers.get('content-length'));
        if (head.ok && Number.isFinite(length) && length > 0) return length;
        // A server that does not say: read it.
        return (await fetchBytes(url)).byteLength;
      },
    );
    if ('problems' in preview) {
      console.warn('wereldpakket ongeldig', preview.problems);
      showWaiting(
        `Wereldpakket ongeldig: ${preview.problems
          .slice(0, 3)
          .map((problem) => `${problem.path} ${problem.message}`)
          .join(' · ')}`,
      );
      return;
    }
    const { served, media, lesson } = preview;
    const prepared = await prepareWorld(served.slug, { [served.slug]: served }, media, fetchBytes);
    const problem = prepared.problems[0];
    if (problem) {
      console.warn(`wereldpakket ongeldig: ${problem.path} ${problem.message}`, prepared.problems);
    }
    playLesson(lesson, {
      media: {},
      cues: {},
      world: {
        world: prepared.source ?? null,
        ...(problem ? { worldNote: `Wereldpakket: ${problem.path} ${problem.message}` } : {}),
      },
    });
    const pictureTo = pictureAddress(location.search);
    if (pictureTo) offerPicture(pictureTo);
  } catch (error) {
    showWaiting(`Het wereldpakket kon niet worden geladen (${(error as Error).message}).`);
  }
}

/**
 * "Maak plaatje" (ADR-059): the kit's `wereld bekijk` of a folder adds `&plaatje=/plaatje`. A button
 * in the desktop preview's top-right corner (DOM, so never in a headset) draws the view the maker
 * walked to at 1600 × 900 (Stage.still: pixel ratio 1, the screen's tone mapping, without the
 * lectern) and posts it as a JPEG; the server keeps it as plaatje.jpg, and says so.
 */
function offerPicture(url: string): void {
  const button = document.createElement('button');
  button.className = 'lesson';
  button.type = 'button';
  button.textContent = 'Maak plaatje';
  button.title = 'Bewaar wat je nu ziet als het plaatje van de wereld (1600 × 900)';
  const note = document.createElement('button');
  note.className = 'lesson';
  note.type = 'button';
  note.disabled = true;
  note.setAttribute('aria-live', 'polite');
  let hideNote: number | undefined;
  const say = (text: string): void => {
    note.textContent = text;
    if (!note.isConnected) button.after(note);
    window.clearTimeout(hideNote);
    hideNote = window.setTimeout(() => note.remove(), 8000);
  };
  button.addEventListener('click', () => {
    // Not a key's later Enter or space: the arrow keys and the rest stay the lesson's.
    button.blur();
    const theStageNow = stage;
    if (!theStageNow || theStageNow.presenting) {
      say('Een plaatje maak je in de desktopweergave, niet in de headset.');
      return;
    }
    button.disabled = true;
    say('Plaatje maken…');
    void (async () => {
      try {
        const { width, height, quality } = PREVIEW_PICTURE;
        const canvas = theStageNow.still(width, height);
        const jpeg = await new Promise<Blob>((resolve, reject) =>
          canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('geen JPEG'))),
            'image/jpeg',
            quality,
          ),
        );
        say(await sendPicture(url, jpeg));
      } catch (error) {
        say(`Het plaatje is niet gemaakt (${(error as Error).message}).`);
      } finally {
        button.disabled = false;
      }
    })();
  });
  cornerStack().append(button);
}

/** `?bekijken=fixture:…` (dev only): Bekijken mode on a laptop, Sluiten back to the lobby. */
async function bootBekijkenFixture(which: string): Promise<void> {
  startAudio('play');
  // `?bekijken=fixture:les`: Steekproef's lesson preview with the first bundled lesson.
  if (which === 'fixture:les') {
    const [first] = await listLessons(undefined);
    if (!first || !headsetMode.previewOffered(false)) return;
    await showLessonPreview(await loadLesson(first, undefined), bekijkenStarts.begin());
    return;
  }
  const { devBekijken } = await import('./dev/bekijken-fixture.js');
  const fixture = await devBekijken(which);
  for (const [url, bytes] of fixture.files) fixtureFiles.set(url, bytes);
  if (!headsetMode.previewOffered(false)) return;
  await showBekijken(fixture.answer, bekijkenStarts.begin());
}

/** A bare line of text, for a preview with nothing to show yet. */
function showWaiting(message: string): void {
  overlay.innerHTML = '';
  const card = document.createElement('div');
  card.className = 'card';
  card.innerHTML = '<p class="hint"></p>';
  card.querySelector('p')!.textContent = message;
  overlay.append(card);
}

// A played lesson first: the address is the one thing that says what this tab
// is for, and a browser that happens to be a paired headset must not start
// behaving as one here -- no check-in, no socket, no lobby. `play` was read
// above, before the service worker.
const preview =
  play ||
  demo ||
  viewer ||
  modelViewer ||
  worldViewer ||
  lessonPicture ||
  testDrive ||
  pakket ||
  bekijkenFixture
    ? undefined
    : previewRequest();
if (demo) {
  void bootDemo(demo);
} else if (play) {
  void bootPlay(play);
} else if (viewer) {
  void bootViewer(viewer);
} else if (modelViewer) {
  void bootModelViewer(modelViewer);
} else if (worldViewer) {
  void bootWorldViewer(worldViewer);
} else if (lessonPicture) {
  void bootLessonPicture(lessonPicture);
} else if (testDrive) {
  void bootTestDrive(testDrive);
} else if (pakket) {
  void bootPackagePreview(pakket);
} else if (bekijkenFixture) {
  void bootBekijkenFixture(bekijkenFixture);
} else if (preview) {
  bootPreview(preview);
} else {
  void boot();
}
